import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../apiClient';
import { SCHEDULABLE_PROJECT_TYPES } from './Configuration';
import { useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { useLocation } from 'react-router-dom';
import { recurrencePeriods, ymd } from './milestoneTrackerLogic';

// Only panels that are safe to land on cold. The write/destructive ones ('clear', the + forms) are
// deliberately absent: a URL shouldn't be able to drop someone into a confirm dialog.
const OPENABLE_PANELS = ['snapshots'];

// Read once on mount, same contract as useProjectDeepLink -- the page owns the panel afterwards.
function usePanelDeepLink() {
  const { search } = useLocation();
  const [value] = useState(() => new URLSearchParams(search).get('panel') || '');
  return value;
}

// ─── SCHEDULE / GANTT ────────────────────────────────────────────────────────
// This page is what retires OnePlan. Everything it renders comes from our own API
// (`GET /gantt/:projectId` returns the whole schedule plus the date window in one call), so there
// is no REACT_APP_*_SOURCE toggle here the way ProjectSetup/SOWIntake have — there is no legacy
// SharePoint or OnePlan path to fall back to. It is gated at the nav/route level instead
// (REACT_APP_SCHEDULING_ENABLED in App.js), because apps/api has no deploy target yet.
//
// The phase names and status/dependency vocabularies come from GET /scheduling/vocabulary rather
// than importing packages/shared directly: react-scripts' ModuleScopePlugin refuses any module
// resolved outside src/, which a symlinked workspace package always is. Fetching it keeps
// packages/shared as the single source of truth without ejecting CRA or duplicating the lists —
// see the comment on VocabularyController for the full reasoning.
//
// The chart is hand-rolled rather than pulling in a Gantt library: PortfolioTimeline.js already
// establishes the idiom (CSS grid, month columns, var(--*) colors) and a dependency would drag in
// its own design system for no gain. Bars are positioned as percentages of the window so the whole
// thing reflows with the container instead of needing a fixed pixel width.

const ROW_HEIGHT = 30;

// Width of the frozen phase/task/activity name column. One constant because the month header's
// spacer, the label column and the scroll-to-today maths all have to agree on it — they were three
// separate 260s before, and a frozen column that disagrees with its own header is worse than none.
const LABEL_WIDTH = 260;

// A horizontally scrolling box can't use `position: sticky` against the page (it becomes
// the sticky scope itself), so the header is nudged down by however far the page's scroller has moved past the
// top of the box -- works whatever the box's height, unlike capping it and scrolling inside.
function usePinnedHeader(boxRef, headerRef) {
  useEffect(() => {
    const box = boxRef.current;
    const header = headerRef.current;
    if (!box || !header) return undefined;
    let page = box.parentElement;
    while (page && !/(auto|scroll)/.test(getComputedStyle(page).overflowY)) page = page.parentElement;
    if (!page) return undefined;
    const update = () => {
      const past = page.getBoundingClientRect().top - box.getBoundingClientRect().top;
      const room = box.offsetHeight - header.offsetHeight;
      header.style.transform = past > 0 ? `translateY(${Math.min(past, room)}px)` : '';
    };
    page.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      page.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [boxRef, headerRef]);
}

// `sticky` rather than a second scroll container synced by JS: the browser keeps it pinned during
// momentum and keyboard scrolling for free. zIndex clears the timeline's own layers — the today
// marker sits at 3 and dependency connectors above the bars — so nothing draws over the names.
const FROZEN_COLUMN = {
  width: LABEL_WIDTH,
  flexShrink: 0,
  position: 'sticky',
  left: 0,
  zIndex: 5,
};

/**
 * A color per band, so phases and workstreams are distinguishable at a glance rather than eight
 * shades of the same navy. Drawn from App.css's palette — no new colors introduced.
 *
 * Indexed by position rather than by name: a project has at most eight phases and a handful of
 * workstreams, and cycling is fine beyond that.
 */
const BAND_COLORS = [
  'var(--primary-dark)',
  'var(--teal)',
  'var(--violet)',
  'var(--green)',
  'var(--primary-light)',
  'var(--yellow)',
  'var(--dark-blue)',
  'var(--gray)',
];

const bandColor = (index) => BAND_COLORS[index % BAND_COLORS.length];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Exported: CustomerProjectSchedule.js's status pills key off this too, so a status added or
// renamed here can't silently fall out of sync between the internal and customer-facing schedules.
export const TASK_STATUS_COLOR = {
  'Not Started': 'var(--gray)',
  'In Progress': 'var(--primary-light)',
  Complete: 'var(--green)',
  Blocked: 'var(--red)',
};

const MILESTONE_STATUS_COLOR = {
  'Not Started': 'var(--gray)',
  'In Progress': 'var(--primary-light)',
  Delivered: 'var(--teal)',
  Accepted: 'var(--green)',
  Billed: 'var(--violet)',
  Paid: 'var(--dark-blue)',
};

/** Diamond size. Bumped from the glyph's default -- a milestone is the thing people look for. */
const MILESTONE_SIZE = 17;

/**
 * How each milestone status draws: a distinct SHAPE, not only a distinct color.
 *
 * Color alone was doing all the work, which fails twice over — on a dense chart the markers are
 * 17px and several palette colors read alike at that size, and roughly one man in twelve can't
 * separate the red/green pair at all. Shape survives both.
 *
 *   Not Started   hollow diamond    — outlined, reads as "nothing has happened yet"
 *   In Progress   half-filled diamond — started, not finished
 *   Delivered     circled check     — handed over, awaiting the customer
 *   Accepted      filled circled check — signed off
 *   Billed        hollow circled $  — invoiced, not yet paid
 *   Paid          filled circled $  — the terminal state for a billable milestone
 *
 * No "Missed" glyph — lateness is read off targetDate vs. actualDate/today, not a status (ADR 0039,
 * same reasoning as ADR 0033's "read history from dates, not statuses").
 */
const MILESTONE_GLYPH = {
  'Not Started': { shape: 'diamond', fill: 'hollow' },
  'In Progress': { shape: 'diamond', fill: 'half' },
  Delivered: { shape: 'circle', mark: '✓', fill: 'hollow' },
  Accepted: { shape: 'circle', mark: '✓', fill: 'solid' },
  Billed: { shape: 'circle', mark: '$', fill: 'hollow' },
  Paid: { shape: 'circle', mark: '$', fill: 'solid' },
};

/** Milestone statuses that mean the deliverable is done — the ones that complete a delivering task. */
const MILESTONE_COMPLETE = new Set(['Delivered', 'Accepted', 'Billed', 'Paid']);

/**
 * A milestone marker, shaped and colored by status.
 *
 * One component rather than the six inline `◆` spans this replaced, so the rules can't drift between
 * the label column, the bars, the collapsed bands and the outline view. `size` scales everything, so
 * a collapsed band can draw smaller markers without re-deriving the geometry.
 *
 * Exported for the Portfolio Timeline's drill-down, which has to draw the same marker for the same
 * status — a milestone that reads Delivered on one page and Not Started on the other is the drift
 * this component exists to prevent. Same precedent as this page importing from Configuration.
 */
export function MilestoneGlyph({ status, size = MILESTONE_SIZE, color, title }) {
  const resolved = color || MILESTONE_STATUS_COLOR[status] || 'var(--violet)';
  const spec = MILESTONE_GLYPH[status] || MILESTONE_GLYPH['Not Started'];

  if (spec.shape === 'diamond') {
    // A rotated square rather than the ◆ character: a glyph can only ever be solid, and "started
    // but not finished" needs a partial fill. Rotating a div also makes the size exact rather than
    // font-dependent.
    const box = Math.round(size * 0.72);
    return (
      <span
        title={title}
        style={{
          display: 'inline-flex',
          width: size,
          height: size,
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
        }}
      >
        <span
          style={{
            width: box,
            height: box,
            transform: 'rotate(45deg)',
            border: `2px solid ${resolved}`,
            boxSizing: 'border-box',
            // Half-filled reads as a diamond whose lower-left triangle is inked. Rotated 45°, a
            // linear-gradient split at 50% lands on the diagonal, which is the effect wanted.
            background:
              spec.fill === 'half'
                ? `linear-gradient(to bottom right, ${resolved} 0 50%, transparent 50% 100%)`
                : 'transparent',
          }}
        />
      </span>
    );
  }

  // A real circle rather than the ✅ emoji: the emoji renders at a different size and color on
  // every platform, and it can't inherit the status color when a date has slipped.
  const solid = spec.fill === 'solid';
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid ${resolved}`,
        background: solid ? resolved : 'var(--surface)',
        color: solid ? 'var(--white)' : resolved,
        fontSize: Math.round(size * 0.6),
        fontWeight: 700,
        lineHeight: 1,
        boxSizing: 'border-box',
        verticalAlign: 'middle',
      }}
    >
      {spec.mark}
    </span>
  );
}

/** The clicked element's on-screen box, as the anchor for an edit popover. */
function rectOf(event) {
  const el = event?.currentTarget;
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const { top, bottom, left, right, width, height } = el.getBoundingClientRect();
  // A plain object, not the live DOMRect: this is stored in state and read after the row may have
  // re-rendered or scrolled, and a DOMRect is a snapshot anyway.
  return { top, bottom, left, right, width, height };
}

const POPOVER_WIDTH = 400;
const POPOVER_GAP = 8;
/** Viewport breathing room, so a popover never sits flush against an edge. */
const POPOVER_MARGIN = 12;

/**
 * An edit panel floated next to the row that opened it, rather than parked at the bottom of the page.
 *
 * The panels used to render below the chart, which meant clicking a task on row 60 of a long
 * schedule scrolled the thing you were editing out of sight (Jeremy, 2026-07-28). Anchoring keeps
 * the row and its editor in the same glance.
 *
 * Positioning is `fixed` and derived from the anchor's viewport box:
 *  - Prefers below-and-left-aligned; flips above when there isn't room underneath.
 *  - Clamps horizontally so a diamond near the right edge doesn't push it off-screen.
 *  - Falls back to centered when there's no anchor (keyboard selection, or a re-render that lost it).
 *
 * Closes on backdrop click and on Escape. The backdrop is transparent rather than dimmed — this is
 * an inline editor on a dense chart, and dimming the chart you're editing against would hide the
 * context that makes the edit make sense.
 */
function EditPopover({ anchor, onClose, children }) {
  const ref = useRef(null);
  // Positioned after measuring, not before. The first attempt guessed the height at a constant and
  // flipped up or down from that -- which put the Save button off the bottom of the window on any
  // panel taller than the guess (Jeremy, 2026-07-28), with no way to scroll to it because the
  // popover itself was the scroll container and its overflow started below the fold.
  const [placement, setPlacement] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // useLayoutEffect, not useEffect: this runs before the browser paints, so the popover never
  // appears at an unplaced position and then jumps.
  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const available = vh - POPOVER_MARGIN * 2;
      // scrollHeight, not offsetHeight: offsetHeight is already capped by maxHeight, so using it
      // would make a too-tall panel look like it fits.
      const height = Math.min(el.scrollHeight, available);
      const width = el.offsetWidth || POPOVER_WIDTH;

      if (!anchor) {
        setPlacement({
          top: Math.max(POPOVER_MARGIN, (vh - height) / 2),
          left: Math.max(POPOVER_MARGIN, (vw - width) / 2),
          maxHeight: available,
        });
        return;
      }

      const roomBelow = vh - anchor.bottom - POPOVER_GAP - POPOVER_MARGIN;
      const roomAbove = anchor.top - POPOVER_GAP - POPOVER_MARGIN;
      // Below unless above genuinely has more room. Below is the natural reading direction, so it
      // only loses when it would actually clip.
      const openUp = height > roomBelow && roomAbove > roomBelow;

      let top = openUp ? anchor.top - POPOVER_GAP - height : anchor.bottom + POPOVER_GAP;
      // The clamp that fixes the reported bug: whatever the anchor and whichever direction was
      // chosen, the panel must end up entirely on screen. If it can't fit either way it pins to the
      // top margin and scrolls internally, so Save is always reachable.
      top = Math.max(POPOVER_MARGIN, Math.min(top, vh - height - POPOVER_MARGIN));

      const left = Math.min(
        Math.max(POPOVER_MARGIN, anchor.left),
        Math.max(POPOVER_MARGIN, vw - width - POPOVER_MARGIN),
      );

      setPlacement({ top, left, maxHeight: available });
    };

    place();
    // Re-place on resize and on scroll: the anchor's viewport box moves when the page scrolls
    // underneath a fixed-position panel, and a resize can invalidate the flip decision.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, children]);

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'transparent' }}
        aria-hidden
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="false"
        className="card"
        style={{
          position: 'fixed',
          zIndex: 41,
          width: POPOVER_WIDTH,
          maxWidth: `calc(100vw - ${POPOVER_MARGIN * 2}px)`,
          // Tall content scrolls inside the popover rather than growing past the viewport.
          maxHeight: placement?.maxHeight ?? `calc(100vh - ${POPOVER_MARGIN * 2}px)`,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          marginBottom: 0,
          boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
          top: placement?.top ?? -9999,
          left: placement?.left ?? -9999,
          // Hidden until measured, so the pre-placement frame isn't visible off-anchor.
          visibility: placement ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </>
  );
}

/**
 * A band's color at low opacity, for shading the rows inside an expanded group.
 *
 * Rendered as a positioned layer rather than a computed color: the palette is CSS custom properties
 * (`var(--teal)`), so there is no hex value here to tint arithmetically, and `color-mix()` would tie
 * this to a browser baseline the rest of the app doesn't assume.
 */
function BandTint({ color, opacity = 0.07 }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        background: color,
        opacity,
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── DATE HELPERS ────────────────────────────────────────────────────────────
// All positioning is done in whole months plus a fractional offset for the day, which is enough
// precision for a multi-month implementation schedule and keeps the month header honest.

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function monthsBetween(from, to) {
  return (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
}

function daysInMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/**
 * Parse a date for positioning, treating a bare `YYYY-MM-DD` as LOCAL midnight.
 *
 * `new Date('2026-01-01')` is UTC midnight and reads back as 31 December west of Greenwich, which
 * would put a bar in the wrong month — and at a year boundary, in the wrong year. The API sends full
 * ISO timestamps so this rarely bites in practice, but the chart shouldn't depend on that: date-only
 * values reach it from date inputs too. Same convention as the API's `parseScheduleDate` (ADR 0009).
 */
function toDate(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  return new Date(value);
}

// Exported for CustomerProjectSchedule.js -- same local-midnight-aware parsing via toDate() below,
// so a date renders identically on both schedules rather than drifting if this format ever changes.
export function fmtDate(value) {
  if (!value) return '—';
  return toDate(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ISO date (yyyy-mm-dd) for <input type="date">, which rejects a full timestamp.
function toDateInput(value) {
  return value ? new Date(value).toISOString().slice(0, 10) : '';
}

/**
 * Projects worth offering in the schedule picker.
 *
 * `Non-Billable Time` is a time-booking bucket carried over from OnePlan, not a project anyone
 * schedules. Matched by name because nothing on Project distinguishes a bucket from real delivery
 * work — there is no `isInternal` flag to key off, and inventing one is a core-module schema change.
 * `T&M` and `Misc (Plug)` are the same shape of thing if this list ever needs to grow.
 */
const NON_SCHEDULABLE_PROJECT_NAMES = new Set(['Non-Billable Time']);

export function selectableProjects(projects, showArchived) {
  return projects.filter(
    (p) =>
      !NON_SCHEDULABLE_PROJECT_NAMES.has(p.name) &&
      // T&M is billed against hours, not against a plan, so there's nothing here to promote. A row
      // with no projectType at all predates the column and is treated as the 'POC' default rather
      // than hidden — hiding rows on a missing field would silently shrink the list.
      SCHEDULABLE_PROJECT_TYPES.includes(p.projectType ?? 'POC') &&
      (showArchived || p.projectStatus !== 'Archived'),
  );
}

/**
 * Text for one `<option>`. Text, because an `<option>` renders nothing else — no badge span, no
 * color — so the "nothing scheduled" marker has to be a character.
 *
 * `scheduledIds` is null until the coverage call answers. Marking everything unscheduled in the
 * meantime would be a visible lie that then flickers away, so nothing is claimed until it's known.
 */
export function optionLabel(project, scheduledIds) {
  const marker = scheduledIds && !scheduledIds.has(project.id) ? '◇ ' : '';
  const account = project.account?.name ? ` · ${project.account.name}` : '';
  // Only meaningful once archived rows are actually being shown, and harmless when they aren't.
  const archived = project.projectStatus === 'Archived' ? ' · Archived' : '';
  return `${marker}${project.name}${account}${archived}`;
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function ProjectSchedule() {
  // Restores whichever project was last selected on any of these pages (ADR 0042), unless a
  // `?project=<id>` link says otherwise (ADR 0038) — a restored/linked project is a project the user
  // already picked, not a guess, so it's not in tension with "no default selection" below. Archived
  // projects are shown when one arrives, so it resolves whatever its status; without that the effect
  // further down would clear the selection it just made.
  const [projectId, setProjectId] = useSelectedProject();

  const [projects, setProjects] = useState([]);
  const [scheduledIds, setScheduledIds] = useState(null); // null until loaded — see optionLabel
  const [showArchived, setShowArchived] = useState(!!projectId);
  const [vocab, setVocab] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // apiFetch returns the PARSED body, not a Response — no .ok, no .json().
        // All three are needed before anything renders, and none depends on the others.
        const [rows, vocabulary, coverage] = await Promise.all([
          apiFetch('/projects'),
          apiFetch('/scheduling/vocabulary'),
          apiFetch('/gantt/scheduled-projects'),
        ]);
        if (cancelled) return;
        setProjects(rows);
        setVocab(vocabulary);
        setScheduledIds(new Set(coverage.projectIds));
        // No default selection (Jeremy, 2026-08-25). Auto-opening the alphabetically-first project
        // presents someone else's schedule as though it were the one you asked for, and the load it
        // triggers is wasted every time that guess is wrong.
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleProjects = useMemo(
    () => selectableProjects(projects, showArchived),
    [projects, showArchived],
  );

  // Un-ticking "show archived" while an archived project is selected would leave the select on a
  // value none of its options carry, which browsers render as a blank box. Drop back to the
  // placeholder rather than to the first project: silently swapping in a different project's
  // schedule is worse than asking again, and this is the same reason nothing is selected on load.
  // The `projects.length > 0` guard is what stops this clearing a `?project=` deep link before the
  // list arrives — see ADR 0038.
  usePruneMissingProject(projectId, setProjectId, visibleProjects, projects.length > 0);

  const loadSchedule = useCallback(async (id) => {
    if (!id) {
      setSchedule(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const next = await apiFetch(`/gantt/${id}`);
      setSchedule(next);
      // Keep the picker's ◇ honest without a second round trip. This reloads after every write, so
      // populating or clearing a schedule moves the marker straight away — the coverage call is only
      // fetched once, on mount.
      const nowHasSchedule = ['phases', 'workstreams', 'tasks', 'milestones'].some(
        (k) => (next[k] || []).length > 0,
      );
      setScheduledIds((prev) => {
        if (!prev) return prev;
        if (prev.has(id) === nowHasSchedule) return prev;
        const updated = new Set(prev);
        if (nowHasSchedule) updated.add(id);
        else updated.delete(id);
        return updated;
      });
    } catch (e) {
      setError(e.message);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedule(projectId);
  }, [projectId, loadSchedule]);

  const refresh = useCallback(() => loadSchedule(projectId), [loadSchedule, projectId]);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Scheduling</div>
        <h1 className="page-title">Project Schedule</h1>
        <p className="page-subtitle">
          Phases, milestones, tasks and dependencies — the replacement for OnePlan scheduling.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 280, maxWidth: 520 }}>
            <label className="form-label" htmlFor="schedule-project">Project</label>
            <select
              id="schedule-project"
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!visibleProjects.length}
            >
              {/* Always present, so the select has a real option to sit on before anything is
                  picked — and so there's a way back to "nothing selected" after picking. */}
              <option value="">
                {visibleProjects.length ? '— Select a project —' : 'No projects yet'}
              </option>
              {visibleProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {optionLabel(p, scheduledIds)}
                </option>
              ))}
            </select>
          </div>
          {/* A checkbox rather than a fourth status filter: archived projects are the exception you
              occasionally need to look back at, not a dimension worth a whole control. */}
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
              color: 'var(--text-mid)', cursor: 'pointer', paddingBottom: 10, whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived projects
          </label>
        </div>
        {/* Explains the marker in the list above rather than leaving "◇" to be guessed at. Only shown
            once the coverage call has answered, so it can't claim anything before it knows. */}
        {scheduledIds && visibleProjects.some((p) => !scheduledIds.has(p.id)) && (
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 8 }}>
            ◇ marks a project with nothing scheduled yet.
          </div>
        )}
      </div>

      {error && (
        <div
          className="card"
          style={{ marginBottom: 20, borderLeft: '4px solid var(--red)' }}
          role="alert"
        >
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            <strong style={{ color: 'var(--red)' }}>Couldn't load the schedule.</strong> {error}
          </div>
          <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={refresh}>
            Try again
          </button>
        </div>
      )}

      {loading && !schedule && (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      )}

      {!loading && !visibleProjects.length && !error && (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          {/* Two different dead ends: nothing exists, or the archive filter hid everything. Saying
              "no projects yet" in the second case would send someone off to SOW Intake for nothing. */}
          {projects.length ? (
            <>
              <div className="placeholder-title">Every project is archived</div>
              <div className="placeholder-sub">
                Tick <strong>Show archived projects</strong> above to see them.
              </div>
            </>
          ) : (
            <>
              <div className="placeholder-title">No projects yet</div>
              <div className="placeholder-sub">
                Commit a SOW in SOW Intake first — a schedule hangs off a project.
              </div>
            </>
          )}
        </div>
      )}

      {!loading && !error && !projectId && visibleProjects.length > 0 && (
        <div className="placeholder-page">
          <div className="placeholder-icon">📋</div>
          <div className="placeholder-title">Pick a project</div>
          <div className="placeholder-sub">
            Choose one above to see its phases, tasks, milestones and dependencies.
          </div>
        </div>
      )}

      {/* Keyed on the project, so switching projects remounts the board and re-seeds it: the
          collapsed sets are seeded once from the schedule's own phase and workstream ids, and
          carrying the previous project's ids across would leave the new one fully expanded. It also
          drops a selected task belonging to a project you've navigated away from. Refreshing after a
          write keeps the same key, so an edit doesn't re-collapse what you just opened. */}
      {schedule && vocab && (
        <ScheduleBoard
          key={schedule.project.id}
          schedule={schedule}
          vocab={vocab}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

// ─── BOARD: chart + editing ──────────────────────────────────────────────────

function ScheduleBoard({ schedule, vocab, onChanged }) {
  const {
    project,
    phases,
    workstreams = [],
    tasks,
    milestones,
    dependencies,
    window: dateWindow,
  } = schedule;
  // 'promote' | 'template' | 'snapshots' | 'clear' | 'phase' | 'task' | 'milestone' | 'dependency'
  // Seeded from ?panel= so a link can land on the panel it means (Project Launch's "View baselines"),
  // read once like the project deep link itself -- closing the panel must not re-open it.
  const deepLinkPanel = usePanelDeepLink();
  const [panel, setPanel] = useState(() => (
    OPENABLE_PANELS.includes(deepLinkPanel) ? deepLinkPanel : null
  ));
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const [view, setView] = useState('phase'); // 'phase' | 'workstream'
  // Both views open fully collapsed (Jeremy, 2026-08-25). A real plan is 100+ rows -- Clark is 105 --
  // so opening expanded drops you into the middle of a wall of activities with no sense of the shape.
  // Collapsed, the bands ARE the overview, and expanding one is a click.
  //
  // Two independent sets, so collapsing in one view doesn't reach into the other. The per-kind
  // `defaultCollapsed` vocabulary no longer decides the initial state, since everything now starts
  // collapsed regardless -- it stays meaningful only as documentation of intent.
  // 'unassigned' is included because both row builders synthesise a trailing Unassigned band under
  // that id; leaving it out would open one stubbornly expanded band in an otherwise collapsed chart.
  const [collapsed, setCollapsed] = useState(
    () => new Set([...workstreams.map((w) => w.id), 'unassigned']),
  );
  const [collapsedPhases, setCollapsedPhases] = useState(
    () => new Set([...phases.map((p) => p.id), 'unassigned']),
  );
  const [actionError, setActionError] = useState('');

  const activeCollapsed = view === 'phase' ? collapsedPhases : collapsed;
  const setActiveCollapsed = view === 'phase' ? setCollapsedPhases : setCollapsed;

  const selectedTask = tasks.find((t) => t.id === selectedTaskId) || null;
  const selectedMilestone = milestones.find((m) => m.id === selectedMilestoneId) || null;

  const toggleCollapsed = (id) =>
    setActiveCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Where the edit popover should appear: the on-screen box of whatever was clicked. Captured at
  // click time rather than looked up later by id, because the row may be a diamond on a bar, a
  // label in the left column, or an outline entry -- all three are "the thing the user pointed at",
  // and only the event knows which.
  const [anchorRect, setAnchorRect] = useState(null);
  // What the last date edit pushed downstream, so a cascade is visible rather than silent.
  const [rescheduleNotice, setRescheduleNotice] = useState(null);

  // Selecting a task closes any open milestone and vice versa — only one edit popover at a time, so
  // there's never a question about which row it refers to.
  const selectTask = (id, event) => {
    setSelectedMilestoneId(null);
    const next = id === selectedTaskId ? null : id;
    setSelectedTaskId(next);
    setAnchorRect(next && event ? rectOf(event) : null);
  };
  const selectMilestone = (id, event) => {
    setSelectedTaskId(null);
    const next = id === selectedMilestoneId ? null : id;
    setSelectedMilestoneId(next);
    setAnchorRect(next && event ? rectOf(event) : null);
  };

  // Any create/update/delete refetches the whole window rather than patching local state: the
  // window itself can move when a date changes, and re-deriving it client-side would drift from
  // what the API computes.
  const afterWrite = async (fn) => {
    setActionError('');
    try {
      const result = await fn();
      // A task date change comes back with whatever it pushed downstream. Reported rather than left
      // for the user to spot rows that moved on their own -- a silent cascade is indistinguishable
      // from a bug.
      const shifts = result?.rescheduled?.shifts;
      setRescheduleNotice(
        shifts?.length
          ? { shifts, truncated: !!result.rescheduled.truncated }
          : null,
      );
      setPanel(null);
      setSelectedTaskId(null);
      setSelectedMilestoneId(null);
      await onChanged();
      return result;
    } catch (e) {
      setActionError(e.message);
      throw e;
    }
  };

  const rows = useMemo(
    () =>
      view === 'phase'
        ? buildPhaseRows(phases, tasks, milestones, collapsedPhases)
        : buildWorkstreamRows(workstreams, tasks, milestones, collapsed),
    [view, phases, workstreams, tasks, milestones, collapsed, collapsedPhases],
  );

  // Collapsing every group is the quickest way to get an overview of a long schedule, which is the
  // point — a promoted Ada County project is 40-odd rows.
  const groupIds = rows.filter((r) => r.collapsible).map((r) => r.id);
  const allCollapsed = groupIds.length > 0 && groupIds.every((id) => activeCollapsed.has(id));
  const toggleAll = () => setActiveCollapsed(allCollapsed ? new Set() : new Set(groupIds));
  const grid = useMemo(() => buildGrid(dateWindow), [dateWindow]);

  // Anything at all in the scheduling tables. Deliberately not just `phases.length`: a schedule with
  // only unassigned milestones still has data worth warning about before wiping it.
  const scheduleCounts = {
    phases: phases.length,
    workstreams: workstreams.length,
    milestones: milestones.length,
    tasks: tasks.length,
    dependencies: dependencies.length,
  };
  const hasSchedule = Object.values(scheduleCounts).some((n) => n > 0);

  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <Stat label="Phases" value={phases.length} />
          <Stat label="Tasks" value={tasks.length} />
          <Stat label="Milestones" value={milestones.length} />
          <Stat label="Dependencies" value={dependencies.length} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* "Populate from SOW" only, and only while there's nothing to populate over --
                promotion is idempotent by title, so pressing it on a populated schedule is mostly a
                no-op. Its old counterpart, "Clear project schedule", moved to the foot of the Plan
                (edit) view (Jeremy, 2026-08-25): a destructive whole-schedule action doesn't belong
                in the toolbar you reach past to add a phase. */}
            {/* Two ways to start a schedule, and both only while there's nothing to start over:
                the SOW where there is one, the methodology template where there isn't (or where the
                SOW lists milestones but no work). */}
            {!hasSchedule && (
              <>
                <button className="btn btn-primary" onClick={() => setPanel('promote')}>
                  Populate from SOW
                </button>
                <button className="btn btn-primary" onClick={() => setPanel('template')}>
                  Create from Template
                </button>
              </>
            )}
            {hasSchedule && (
              <button className="btn btn-outline" onClick={() => setPanel('snapshots')}>
                Baselines &amp; snapshots
              </button>
            )}
            <button className="btn btn-outline" onClick={() => setPanel('phase')}>+ Phase</button>
            <button
              className="btn btn-outline"
              onClick={() => setPanel('task')}
              disabled={!phases.length}
              title={phases.length ? undefined : 'Add a phase first'}
            >
              + Task
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setPanel('milestone')}
              disabled={!phases.length}
              title={phases.length ? undefined : 'Add a phase first'}
            >
              + Milestone
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setPanel('dependency')}
              disabled={tasks.length < 2}
              title={tasks.length < 2 ? 'Needs at least two tasks' : undefined}
            >
              + Dependency
            </button>
          </div>
        </div>
      </div>

      {actionError && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--red)' }} role="alert">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            <strong style={{ color: 'var(--red)' }}>That didn't work.</strong> {actionError}
          </div>
        </div>
      )}

      {/* A date change that moved other rows says so. Dismissible rather than auto-hiding: the list
          is the record of what just happened to the plan, and a toast that vanishes in three seconds
          is no use if you looked away. */}
      {rescheduleNotice && (
        <div
          className="card"
          style={{ marginBottom: 20, borderLeft: '4px solid var(--primary-light)' }}
          role="status"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
              <strong style={{ color: 'var(--heading)' }}>
                {rescheduleNotice.shifts.length} dependent task
                {rescheduleNotice.shifts.length === 1 ? '' : 's'} moved.
              </strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {rescheduleNotice.shifts.slice(0, 8).map((s) => (
                  <li key={s.taskId} style={{ marginBottom: 2 }}>
                    {s.title || 'Task'} → {fmtDate(s.startDate)} – {fmtDate(s.endDate)}{' '}
                    <span style={{ color: 'var(--text-light)' }}>
                      (+{s.shiftedDays} day{s.shiftedDays === 1 ? '' : 's'})
                    </span>
                  </li>
                ))}
              </ul>
              {/* Never silently truncate a list of things that changed in the database. */}
              {rescheduleNotice.shifts.length > 8 && (
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  …and {rescheduleNotice.shifts.length - 8} more.
                </div>
              )}
              {rescheduleNotice.truncated && (
                <div style={{ marginTop: 6, color: 'var(--yellow)' }}>
                  ⚠ The chain stopped early — the dependency links appear to loop. Check them before
                  relying on these dates.
                </div>
              )}
            </div>
            <button
              className="btn btn-outline"
              style={{ fontSize: 11, padding: '3px 10px', alignSelf: 'flex-start' }}
              onClick={() => setRescheduleNotice(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {panel === 'promote' && (
        <PromoteFromSowPanel
          projectId={project.id}
          onCancel={() => setPanel(null)}
          onApplied={async () => {
            setPanel(null);
            await onChanged();
          }}
        />
      )}
      {panel === 'template' && (
        <CreateFromTemplatePanel
          projectId={project.id}
          onCancel={() => setPanel(null)}
          onApplied={async () => {
            setPanel(null);
            await onChanged();
          }}
        />
      )}
      {panel === 'snapshots' && (
        <SnapshotsPanel projectId={project.id} onCancel={() => setPanel(null)} />
      )}
      {panel === 'clear' && (
        <ConfirmClearSchedule
          projectName={project.name}
          counts={scheduleCounts}
          onCancel={() => setPanel(null)}
          onConfirm={() =>
            afterWrite(() =>
              apiFetch(`/scheduling/schedule/${project.id}`, { method: 'DELETE' }),
            )
          }
        />
      )}
      {panel === 'phase' && (
        <AddPhaseForm
          projectId={project.id}
          allPhases={vocab.phases}
          existingTitles={phases.map((p) => p.title)}
          onCancel={() => setPanel(null)}
          onSubmit={(body) => afterWrite(() => apiFetch('/phases', { method: 'POST', body: JSON.stringify(body) }))}
        />
      )}
      {panel === 'task' && (
        <AddTaskForm
          projectId={project.id}
          phases={phases}
          tasks={tasks}
          milestones={milestones}
          statuses={vocab.taskStatuses}
          onCancel={() => setPanel(null)}
          onSubmit={(body) => afterWrite(() => apiFetch('/tasks', { method: 'POST', body: JSON.stringify(body) }))}
        />
      )}
      {panel === 'milestone' && (
        <AddMilestoneForm
          projectId={project.id}
          phases={phases}
          statuses={vocab.milestoneStatuses}
          onCancel={() => setPanel(null)}
          onSubmit={(body) => afterWrite(() => apiFetch('/milestones', { method: 'POST', body: JSON.stringify(body) }))}
        />
      )}
      {panel === 'dependency' && (
        <AddDependencyForm
          tasks={tasks}
          types={vocab.dependencyTypes}
          onCancel={() => setPanel(null)}
          onSubmit={(body) =>
            afterWrite(() => apiFetch('/task-dependencies', { method: 'POST', body: JSON.stringify(body) }))
          }
        />
      )}

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div className="card-title" style={{ marginBottom: 0 }}>{project.name}</div>
            {groupIds.length > 0 && (
              <button
                className="btn btn-outline"
                style={{ fontSize: 11, padding: '3px 10px' }}
                onClick={toggleAll}
              >
                {allCollapsed ? 'Expand all' : 'Collapse all'}
              </button>
            )}
          </div>
          {/* Two views of one dataset: group the same rows by phase, or by workstream. Milestones
              appear in both, because each carries a phaseId and a workstreamId. */}
          <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {[
              { key: 'phase', label: 'Phase view' },
              { key: 'workstream', label: 'Workstream view' },
              { key: 'plan', label: 'Plan (edit)' },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setView(option.key)}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '6px 14px',
                  border: 'none',
                  cursor: 'pointer',
                  background: view === option.key ? 'var(--primary-dark)' : 'var(--surface)',
                  color: view === option.key ? 'var(--white)' : 'var(--text-mid)',
                }}
                aria-pressed={view === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {view === 'plan' ? (
          <>
            <PlanTable
              projectId={project.id}
              phases={phases}
              tasks={tasks}
              milestones={milestones}
              workstreams={workstreams}
              dependencies={dependencies}
              statuses={vocab.taskStatuses}
              milestoneStatuses={vocab.milestoneStatuses}
              dependencyTypes={vocab.dependencyTypes}
              onWrite={afterWrite}
            />
            {/* Below the table, in the edit view only: you have to have scrolled the whole plan to
                reach it, which is the right amount of friction for wiping every row. */}
            {hasSchedule && (
              <div
                style={{
                  marginTop: 24,
                  paddingTop: 16,
                  borderTop: '1px solid var(--border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  className="btn btn-outline"
                  style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                  onClick={() => setPanel('clear')}
                >
                  Clear project schedule
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Deletes every phase, workstream, task, milestone and dependency on this project.
                  You'll get a confirmation with the counts first.
                </span>
              </div>
            )}
          </>
        ) : (
        <>
        {view === 'workstream' && !workstreams.length && (
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 10 }}>
            No workstreams yet — "Populate from SOW" creates them from the SOW's Services Included.
            Anything without one shows under Unassigned below.
          </div>
        )}
        {!rows.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-mid)', fontStyle: 'italic', padding: '12px 0' }}>
            Nothing scheduled yet. Add a phase to start laying out the schedule.
          </div>
        ) : !grid ? (
          // Nothing is dated, so there is no axis to draw a chart against. An outline is still a
          // useful view of the same rows -- and this is the normal state right after populating from
          // a SOW that gave no dates, so an empty panel here would look like a failure.
          <OutlineView
            rows={rows}
            selectedTaskId={selectedTaskId}
            selectedMilestoneId={selectedMilestoneId}
            onSelectTask={selectTask}
            onSelectMilestone={selectMilestone}
            onToggleCollapsed={toggleCollapsed}
          />
        ) : (
          <GanttChart
            rows={rows}
            grid={grid}
            view={view}
            dependencies={dependencies}
            selectedTaskId={selectedTaskId}
            selectedMilestoneId={selectedMilestoneId}
            onSelectTask={selectTask}
            onSelectMilestone={selectMilestone}
            onToggleCollapsed={toggleCollapsed}
          />
        )}
        </>
        )}
      </div>

      {selectedMilestone && (
        <EditPopover anchor={anchorRect} onClose={() => setSelectedMilestoneId(null)}>
        <EditMilestonePanel
          milestone={selectedMilestone}
          phases={phases}
          workstreams={workstreams}
          statuses={vocab.milestoneStatuses}
          deliveringTask={tasks.find((t) => t.milestoneId === selectedMilestone.id) || null}
          onClose={() => setSelectedMilestoneId(null)}
          // Two writes, sequenced rather than batched: there is no endpoint that updates a milestone
          // and a task together, and inventing one for this would put a scheduling concern into the
          // API for a convenience the UI can express. The milestone goes first, so if the task write
          // fails the milestone edit the user actually made is still saved.
          onSave={(body, taskPatch) =>
            afterWrite(async () => {
              await apiFetch(`/milestones/${selectedMilestone.id}`, {
                method: 'PATCH',
                body: JSON.stringify(body),
              });
              const deliveringTask = tasks.find((t) => t.milestoneId === selectedMilestone.id);
              if (taskPatch && deliveringTask) {
                await apiFetch(`/tasks/${deliveringTask.id}`, {
                  method: 'PATCH',
                  body: JSON.stringify(taskPatch),
                });
              }
            })
          }
          onDelete={() =>
            afterWrite(() => apiFetch(`/milestones/${selectedMilestone.id}`, { method: 'DELETE' }))
          }
        />
        </EditPopover>
      )}

      {selectedTask && (
        <EditPopover anchor={anchorRect} onClose={() => setSelectedTaskId(null)}>
        <EditTaskPanel
          task={selectedTask}
          phases={phases}
          workstreams={workstreams}
          statuses={vocab.taskStatuses}
          dependencies={dependencies}
          tasks={tasks}
          onClose={() => setSelectedTaskId(null)}
          onSave={(body) =>
            afterWrite(() =>
              apiFetch(`/tasks/${selectedTask.id}`, { method: 'PATCH', body: JSON.stringify(body) }),
            )
          }
          onDelete={() => afterWrite(() => apiFetch(`/tasks/${selectedTask.id}`, { method: 'DELETE' }))}
          onDeleteDependency={(id) =>
            afterWrite(() => apiFetch(`/task-dependencies/${id}`, { method: 'DELETE' }))
          }
        />
        </EditPopover>
      )}
    </>
  );
}

/**
 * The plan editing view — a spreadsheet grid, in the idiom of MS Project's task sheet.
 *
 * The Gantt views are for reading a schedule; this is for building one. Laying dates out across
 * twenty tasks by opening twenty popovers is miserable, so here every field is a cell you tab
 * through: phase, name, start, end, predecessors, % and status.
 *
 * Editing model — **commit on blur, not on keystroke**. A `PATCH` per character would flood the API
 * and, worse, each date write triggers a dependency cascade, so a half-typed year like `2` would
 * push the whole downstream chain to the year 2 before you finished typing. Selects commit
 * immediately, because a select has no half-finished state.
 *
 * Rows are ordered parent-first, sub-tasks nested directly beneath (`Task.parentTaskId`), each
 * sibling group sorted by phase sequence, then `Task.sortOrder`, then start date — how a plan reads
 * top to bottom, and how an undated task still holds the spot it was put in. The row number in the
 * first column is what the Predecessor column refers to — again matching MS Project, where
 * dependencies are cited by row. The ⠿ handle drags a row to a new spot, the ▲/▼ buttons nudge it
 * one at a time -- both restamp `sortOrder` for the sibling group; a drop onto a different phase/
 * parent group is a no-op, since re-parenting is a bigger decision than a reorder. ⇥/⇤ indent/
 * outdent a row, MS Project's convention for building that hierarchy (Tab itself is already spoken
 * for as cell-to-cell focus).
 */
function PlanTable({
  projectId,
  phases,
  tasks,
  milestones,
  workstreams = [],
  dependencies,
  statuses,
  milestoneStatuses = [],
  dependencyTypes,
  onWrite,
}) {
  // Id of a just-duplicated row, so its name field can take focus once the refetch renders it.
  const [focusId, setFocusId] = useState(null);
  const [showMilestones, setShowMilestones] = useState(true);
  // Which cell is mid-edit, and its uncommitted text. One cell at a time is all a keyboard can do,
  // and holding a single draft avoids reconciling a map of drafts against refetched rows.
  const [draft, setDraft] = useState(null); // { taskId, field, value }
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPhaseId, setNewPhaseId] = useState(phases[0]?.id || '');
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const planScrollRef = useRef(null);
  const planHeadRef = useRef(null);
  usePinnedHeader(planScrollRef, planHeadRef);

  const phaseOrder = new Map(phases.map((p, i) => [p.id, i]));

  // Parents before children, each sibling group sorted by phase/date -- same spine the Gantt draws
  // (buildPhaseRows), but keyed only on parentTaskId here so a sub-task doesn't have to share its
  // parent's phase to stay nested under it in this grid.
  const rows = useMemo(() => {
    const byParent = new Map();
    tasks.forEach((t) => {
      const key = t.parentTaskId || null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(t);
    });
    const bySpine = (a, b) => {
      // Unassigned last: a task with no phase isn't part of the plan's spine yet.
      const pa = a.phaseId ? (phaseOrder.get(a.phaseId) ?? 998) : 999;
      const pb = b.phaseId ? (phaseOrder.get(b.phaseId) ?? 998) : 999;
      if (pa !== pb) return pa - pb;
      // Explicit placement wins over date guessing -- this is the only way an undated task (the
      // common case while a plan is still being laid out) holds the spot it was typed into.
      const sa = a.sortOrder ?? 0;
      const sb = b.sortOrder ?? 0;
      if (sa !== sb) return sa - sb;
      const da = a.startDate ? +new Date(a.startDate) : Infinity;
      const db = b.startDate ? +new Date(b.startDate) : Infinity;
      if (da !== db) return da - db;
      return (a.title || '').localeCompare(b.title || '');
    };
    const out = [];
    const visit = (parentId, depth) => {
      (byParent.get(parentId) || []).sort(bySpine).forEach((task) => {
        out.push({ task, depth });
        visit(task.id, depth + 1);
      });
    };
    visit(null, 0);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, phases]);

  const ordered = useMemo(() => rows.map((r) => r.task), [rows]);

  // Tasks and milestones in one list. A milestone lands after the last top-level task of its phase
  // (unassigned last), by target date among its peers -- a phase reads as its work, then its
  // deliverables. Only task rows are numbered: predecessors cite tasks, never milestones.
  const display = useMemo(() => {
    const key = (id) => (id ? (phaseOrder.get(id) ?? 998) : 999);
    const pending = (showMilestones ? milestones : [])
      .map((m) => ({ kind: 'milestone', milestone: m, key: key(m.phaseId) }))
      .sort(
        (a, b) =>
          a.key - b.key ||
          (a.milestone.targetDate ? +new Date(a.milestone.targetDate) : Infinity) -
            (b.milestone.targetDate ? +new Date(b.milestone.targetDate) : Infinity),
      );
    const out = [];
    rows.forEach(({ task, depth }) => {
      if (depth === 0) {
        const k = key(task.phaseId);
        while (pending.length && pending[0].key < k) out.push(pending.shift());
      }
      out.push({ kind: 'task', task, depth });
    });
    return out.concat(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, milestones, phases, showMilestones]);

  useEffect(() => {
    if (!focusId) return;
    const input = document.querySelector(`[data-plan-row="${focusId}"] input`);
    if (input) {
      input.focus();
      input.select();
      setFocusId(null);
    }
  }, [focusId, tasks, milestones]);

  // Row number per task id, for the Predecessor column to cite.
  const rowNumber = new Map(ordered.map((t, i) => [t.id, i + 1]));

  // `onWrite` rethrows so its caller can react, but every failure is already on screen as the
  // page-level error banner. Swallowing here keeps a failed cell edit from becoming an unhandled
  // rejection in an onChange handler.
  const safeWrite = useCallback(
    async (fn) => {
      try {
        return await onWrite(fn);
      } catch {
        return null;
      }
    },
    [onWrite],
  );

  const patch = async (taskId, body) => {
    setBusyTaskId(taskId);
    try {
      await safeWrite(() =>
        apiFetch(`/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(body) }),
      );
    } finally {
      setBusyTaskId(null);
    }
  };

  // The API refuses (409, named blockers) a task that still has child tasks or dependency links --
  // window.confirm here is the grid's only other destructive action, matching the same row-delete
  // convention as the register grids (RaaiddRegister, BilledAsUsedRegister). The API's own check
  // doesn't know about milestoneId, though: a task can be childless and dependency-free and still be
  // the one EditMilestonePanel auto-completes when its milestone is marked delivered, so deleting it
  // silently breaks that link with nothing to say so afterward -- named here instead.
  const removeTask = async (task) => {
    const milestone = milestones.find((m) => m.id === task.milestoneId);
    const warning = milestone
      ? ` It delivers the "${milestone.title}" milestone — deleting it removes that link too.`
      : '';
    if (!window.confirm(`Delete "${task.title || 'this task'}"?${warning} This can't be undone.`)) return;
    setBusyTaskId(task.id);
    try {
      await safeWrite(() => apiFetch(`/tasks/${task.id}`, { method: 'DELETE' }));
    } finally {
      setBusyTaskId(null);
    }
  };

  const patchMilestone = async (milestone, body) => {
    setBusyTaskId(milestone.id);
    try {
      await safeWrite(async () => {
        const result = await apiFetch(`/milestones/${milestone.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        // Same rule as the edit popover: a delivered milestone completes the task that delivers it.
        const deliverer = tasks.find((t) => t.milestoneId === milestone.id);
        if (
          MILESTONE_COMPLETE.has(body.status) &&
          deliverer &&
          (deliverer.status !== 'Complete' || deliverer.percentComplete !== 100)
        ) {
          await apiFetch(`/tasks/${deliverer.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: 'Complete', percentComplete: 100 }),
          });
        }
        return result;
      });
    } finally {
      setBusyTaskId(null);
    }
  };

  const removeMilestone = async (milestone) => {
    if (!window.confirm(`Delete milestone "${milestone.title}"? This can't be undone.`)) return;
    setBusyTaskId(milestone.id);
    try {
      await safeWrite(() => apiFetch(`/milestones/${milestone.id}`, { method: 'DELETE' }));
    } finally {
      setBusyTaskId(null);
    }
  };

  // Duplicate = a fresh, not-started sibling with the same placement and dates, named "… (copy)" and
  // focused for renaming. Predecessor links, the delivered milestone and progress are deliberately
  // not carried over: they describe the original's history, not a template for new data entry.
  const duplicateTask = async (task) => {
    setBusyTaskId(task.id);
    try {
      const created = await safeWrite(() =>
        apiFetch('/tasks', {
          method: 'POST',
          body: JSON.stringify({
            projectId,
            title: `${task.title || 'Task'} (copy)`,
            ...(task.phaseId ? { phaseId: task.phaseId } : {}),
            ...(task.workstreamId ? { workstreamId: task.workstreamId } : {}),
            ...(task.parentTaskId ? { parentTaskId: task.parentTaskId } : {}),
            ...(task.description ? { description: task.description } : {}),
            ...(task.startDate ? { startDate: toDateInput(task.startDate) } : {}),
            ...(task.endDate ? { endDate: toDateInput(task.endDate) } : {}),
            status: statuses?.[0] || 'Not Started',
            sortOrder: (task.sortOrder ?? 0) + 1,
          }),
        }),
      );
      if (created?.id) setFocusId(created.id);
    } finally {
      setBusyTaskId(null);
    }
  };

  const duplicateMilestone = async (m) => {
    setBusyTaskId(m.id);
    try {
      const created = await safeWrite(() =>
        apiFetch('/milestones', {
          method: 'POST',
          body: JSON.stringify({
            projectId,
            title: `${m.title} (copy)`,
            ...(m.phaseId ? { phaseId: m.phaseId } : {}),
            ...(m.workstreamId ? { workstreamId: m.workstreamId } : {}),
            ...(m.targetDate ? { targetDate: toDateInput(m.targetDate) } : {}),
            ...(m.acceptanceCriteria ? { acceptanceCriteria: m.acceptanceCriteria } : {}),
            ...(m.billingCategory ? { billingCategory: m.billingCategory } : {}),
            ...(m.valueType ? { valueType: m.valueType } : {}),
            status: milestoneStatuses[0],
          }),
        }),
      );
      if (created?.id) setFocusId(created.id);
    } finally {
      setBusyTaskId(null);
    }
  };

  /** The row directly above `task` at the same depth -- indenting makes it the new parent. */
  const previousSibling = (task) => {
    const idx = rows.findIndex((r) => r.task.id === task.id);
    const depth = rows[idx].depth;
    for (let i = idx - 1; i >= 0; i--) {
      if (rows[i].depth === depth) return rows[i].task;
      if (rows[i].depth < depth) return null;
    }
    return null;
  };

  /** The row directly below `task` at the same depth -- the ▼ move button's target. */
  const nextSibling = (task) => {
    const idx = rows.findIndex((r) => r.task.id === task.id);
    const depth = rows[idx].depth;
    for (let i = idx + 1; i < rows.length; i++) {
      if (rows[i].depth === depth) return rows[i].task;
      if (rows[i].depth < depth) return null;
    }
    return null;
  };

  // MS Project's Tab/Shift+Tab convention, as buttons -- Tab itself already moves focus between
  // cells here. The API rejects anything that would close a hierarchy cycle.
  const indent = (task) => {
    const parent = previousSibling(task);
    if (!parent) return;
    // A child whose phase doesn't match its parent's is invisible on the Gantt (its tree walk keys
    // on phaseId + parentTaskId together, see buildPhaseRows) -- carry the phase along, including
    // clearing it when the parent itself has none, so indenting can't silently orphan the row there.
    let phaseChange = {};
    if (parent.phaseId && parent.phaseId !== task.phaseId) phaseChange = { phaseId: parent.phaseId };
    else if (!parent.phaseId && task.phaseId) phaseChange = { clearPhase: true };
    patch(task.id, { parentTaskId: parent.id, ...phaseChange });
  };

  const outdent = (task) => {
    if (!task.parentTaskId) return;
    const parent = tasks.find((t) => t.id === task.parentTaskId);
    const grandparentId = parent?.parentTaskId || null;
    patch(task.id, grandparentId ? { parentTaskId: grandparentId } : { clearParent: true });
  };

  const siblingsOf = (task) => {
    const parentId = task.parentTaskId || null;
    return rows.filter((r) => (r.task.parentTaskId || null) === parentId).map((r) => r.task);
  };

  // Re-stamps the whole sibling group with fresh, evenly-spaced sortOrder values rather than just
  // writing the two/one rows that moved -- a plain swap is a no-op whenever both sides still tie at
  // the bySpine fallback (the common case for undated tasks, which otherwise sort by title). This
  // also quietly backfills sortOrder for any older sibling that never had one.
  const restampSiblings = async (triggerTaskId, reordered) => {
    setBusyTaskId(triggerTaskId);
    try {
      await Promise.all(
        reordered
          .map((t, i) => ({ t, sortOrder: (i + 1) * 10 }))
          .filter(({ t, sortOrder }) => t.sortOrder !== sortOrder)
          .map(({ t, sortOrder }) =>
            safeWrite(() =>
              apiFetch(`/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder }) }),
            ),
          ),
      );
    } finally {
      setBusyTaskId(null);
    }
  };

  const moveTask = (task, direction) => {
    const siblings = siblingsOf(task);
    const idx = siblings.findIndex((t) => t.id === task.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const reordered = siblings.slice();
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    return restampSiblings(task.id, reordered);
  };

  // Drag-and-drop is the ▲/▼ buttons' sibling-group rule, just with an arbitrary target instead of
  // the adjacent row -- dropping onto a row from a different phase/parent is a no-op rather than an
  // implicit re-parent, since that's a bigger decision than a reorder.
  const dropTask = (task, targetTask) => {
    if (task.id === targetTask.id) return;
    const siblings = siblingsOf(task);
    const fromIdx = siblings.findIndex((t) => t.id === task.id);
    const toIdx = siblings.findIndex((t) => t.id === targetTask.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = siblings.slice();
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    return restampSiblings(task.id, reordered);
  };

  /** Commit a text/date cell if it actually changed, then drop the draft either way. */
  const commit = (task, field, original) => {
    const value = draft?.value ?? '';
    setDraft(null);
    if (value === (original ?? '')) return;
    // An emptied date clears nothing server-side -- PATCH ignores undefined -- so it's simply not
    // sent. Clearing a date isn't wired up here; it needs its own explicit flag like clearPhase.
    if ((field === 'startDate' || field === 'endDate') && !value) return;
    if (field === 'title' && !value.trim()) return;
    patch(task.id, { [field]: field === 'title' ? value.trim() : value });
  };

  const cellValue = (task, field, original) =>
    draft && draft.taskId === task.id && draft.field === field ? draft.value : (original ?? '');

  const inputStyle = {
    width: '100%',
    border: '1px solid transparent',
    background: 'transparent',
    // Form controls don't inherit `color` from their ancestors by default (UA stylesheet), so
    // without this every cell renders default black-on-transparent text -- invisible once the
    // surface underneath goes dark.
    color: 'var(--text-dark)',
    font: 'inherit',
    fontSize: 12,
    padding: '4px 6px',
    borderRadius: 3,
  };

  const cellStyle = { padding: '2px 4px', verticalAlign: 'middle', borderTop: '1px solid var(--border)' };

  const indentButtonStyle = {
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'var(--text-mid)',
    cursor: 'pointer',
    padding: '1px 3px',
    fontSize: 10,
    lineHeight: 1,
    borderRadius: 3,
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    // Lands after every existing top-level task in the same phase, so an undated task appends to
    // the bottom of the group instead of falling back to bySpine's alphabetical tiebreak.
    const effectivePhaseId = newPhaseId || null;
    const siblingOrders = tasks
      .filter((t) => !t.parentTaskId && (t.phaseId || null) === effectivePhaseId)
      .map((t) => t.sortOrder ?? 0);
    const sortOrder = siblingOrders.length ? Math.max(...siblingOrders) + 10 : 10;
    await safeWrite(() =>
      apiFetch('/tasks', {
        method: 'POST',
        body: JSON.stringify({
          projectId,
          title: newTitle.trim(),
          ...(newPhaseId ? { phaseId: newPhaseId } : {}),
          status: statuses?.[0] || 'Not Started',
          sortOrder,
        }),
      }),
    );
    setNewTitle('');
    setAdding(false);
  };

  // Shared by task and milestone rows -- both carry phaseId/workstreamId and the same clear flags.
  const workstreamSelect = (row, write) => (
    <select
      style={{ ...inputStyle, borderColor: 'var(--border)' }}
      value={row.workstreamId || ''}
      disabled={busyTaskId === row.id}
      onChange={(e) =>
        write(e.target.value ? { workstreamId: e.target.value } : { clearWorkstream: true })
      }
    >
      <option value="">— none —</option>
      {workstreams.map((w) => (
        <option key={w.id} value={w.id}>{w.name}</option>
      ))}
    </select>
  );

  const commitMilestone = (m, field, original) => {
    const value = draft?.value ?? '';
    setDraft(null);
    if (value === (original ?? '')) return;
    if (field === 'targetDate' && !value) return;
    if (field === 'title' && !value.trim()) return;
    patchMilestone(m, { [field]: field === 'title' ? value.trim() : value });
  };

  // Milestones read as landmarks, not work: violet band with a left rule, a status-shaped diamond in
  // place of a row number, bold name. Same violet as the Gantt diamonds and the edit popover.
  const renderMilestoneRow = (m) => {
    const busy = busyTaskId === m.id;
    const deliverer = tasks.find((t) => t.milestoneId === m.id);
    const milestoneCell = { ...cellStyle, borderTop: '1px solid var(--violet)' };
    return (
      <tr
        key={`m-${m.id}`}
        data-plan-row={m.id}
        style={{ opacity: busy ? 0.6 : 1 }}
      >
        <td style={{ ...milestoneCell, textAlign: 'center', borderLeft: '4px solid var(--violet)' }}>
          <MilestoneGlyph status={m.status} size={15} title={`Milestone — ${m.status || 'Not Started'}`} />
        </td>
        <td style={milestoneCell}>
          <select
            style={{ ...inputStyle, borderColor: 'var(--border)' }}
            value={m.phaseId || ''}
            disabled={busy}
            onChange={(e) =>
              patchMilestone(m, e.target.value ? { phaseId: e.target.value } : { clearPhase: true })
            }
          >
            <option value="">— unassigned —</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </td>
        <td style={milestoneCell}>{workstreamSelect(m, (body) => patchMilestone(m, body))}</td>
        <td style={milestoneCell}>
          <input
            style={{ ...inputStyle, fontWeight: 700, color: 'var(--violet)' }}
            value={cellValue(m, 'title', m.title)}
            disabled={busy}
            onFocus={() => setDraft({ taskId: m.id, field: 'title', value: m.title || '' })}
            onChange={(e) => setDraft({ taskId: m.id, field: 'title', value: e.target.value })}
            onBlur={() => commitMilestone(m, 'title', m.title)}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          />
        </td>
        <td style={{ ...milestoneCell, color: 'var(--text-light)', textAlign: 'center' }}>◆</td>
        <td style={milestoneCell}>
          <input
            type="date"
            style={inputStyle}
            value={cellValue(m, 'targetDate', toDateInput(m.targetDate))}
            disabled={busy}
            onFocus={() => setDraft({ taskId: m.id, field: 'targetDate', value: toDateInput(m.targetDate) })}
            onChange={(e) => setDraft({ taskId: m.id, field: 'targetDate', value: e.target.value })}
            onBlur={() => commitMilestone(m, 'targetDate', toDateInput(m.targetDate))}
          />
        </td>
        <td style={{ ...milestoneCell, fontSize: 11, color: 'var(--text-mid)' }}>
          {deliverer ? `Delivered by #${rowNumber.get(deliverer.id)} ${deliverer.title}` : 'Milestone'}
        </td>
        <td style={milestoneCell} />
        <td style={milestoneCell}>
          <select
            style={{ ...inputStyle, borderColor: 'var(--border)' }}
            value={m.status || ''}
            disabled={busy}
            onChange={(e) => patchMilestone(m, { status: e.target.value })}
          >
            {milestoneStatuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </td>
        <td style={{ ...milestoneCell, textAlign: 'center', whiteSpace: 'nowrap' }}>
          <button
            type="button"
            onClick={() => duplicateMilestone(m)}
            disabled={busy}
            title="Duplicate — adds a copy, ready to rename"
            style={{ ...indentButtonStyle, marginRight: 3, padding: '1px 5px', fontSize: 12 }}
          >
            ⧉
          </button>
          <button
            type="button"
            onClick={() => removeMilestone(m)}
            disabled={busy}
            title="Delete milestone"
            style={{ ...indentButtonStyle, color: 'var(--red)', borderColor: 'var(--red)' }}
          >
            ✕
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ marginTop: 8 }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-mid)', marginBottom: 8, cursor: 'pointer' }}>
        <input type="checkbox" checked={showMilestones} onChange={(e) => setShowMilestones(e.target.checked)} />
        Show milestones ({milestones.length})
      </label>
      {/* Wide grid, so it scrolls inside its own container rather than making the page scroll. */}
      <div ref={planScrollRef} style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1150, fontSize: 12 }}>
          <thead ref={planHeadRef} style={{ position: 'relative', zIndex: 2 }}>
            <tr style={{ background: 'var(--light-bg)', textAlign: 'left' }}>
              {[
                ['#', 40],
                ['Phase', 170],
                ['Workstream', 150],
                ['Task / milestone', null],
                ['Start', 130],
                ['End / target', 130],
                ['Predecessors', 210],
                ['%', 60],
                ['Status', 130],
                ['', 64],
              ].map(([label, width]) => (
                <th
                  key={label}
                  style={{
                    padding: '8px 8px',
                    fontWeight: 600,
                    width: width || undefined,
                    whiteSpace: 'nowrap',
                    background: 'var(--light-bg)',
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {display.map((item) => {
              if (item.kind === 'milestone') return renderMilestoneRow(item.milestone);
              const { task, depth } = item;
              const i = rowNumber.get(task.id) - 1;
              const milestone = milestones.find((m) => m.id === task.milestoneId);
              const preds = dependencies.filter((d) => d.successorId === task.id);
              const indentTarget = previousSibling(task);
              const moveDownTarget = nextSibling(task);
              return (
                <tr
                  key={task.id}
                  data-plan-row={task.id}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const sourceId = e.dataTransfer.getData('text/plain') || draggedTaskId;
                    const source = tasks.find((t) => t.id === sourceId);
                    if (source) dropTask(source, task);
                    setDraggedTaskId(null);
                  }}
                  style={{
                    background: busyTaskId === task.id ? 'var(--light-bg)' : 'transparent',
                    opacity: draggedTaskId === task.id ? 0.4 : 1,
                  }}
                >
                  <td style={{ ...cellStyle, color: 'var(--text-mid)', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                      <span
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', task.id);
                          setDraggedTaskId(task.id);
                        }}
                        onDragEnd={() => setDraggedTaskId(null)}
                        title="Drag to reorder"
                        style={{ cursor: 'grab', color: 'var(--text-mid)' }}
                      >
                        ⠿
                      </span>
                      {i + 1}
                    </div>
                  </td>

                  <td style={cellStyle}>
                    <select
                      style={{ ...inputStyle, borderColor: 'var(--border)' }}
                      value={task.phaseId || ''}
                      disabled={busyTaskId === task.id}
                      onChange={(e) =>
                        patch(
                          task.id,
                          e.target.value ? { phaseId: e.target.value } : { clearPhase: true },
                        )
                      }
                    >
                      <option value="">— unassigned —</option>
                      {phases.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </td>

                  <td style={cellStyle}>
                    {workstreamSelect(task, (body) => patch(task.id, body))}
                  </td>

                  <td style={cellStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => moveTask(task, -1)}
                          disabled={!indentTarget || busyTaskId === task.id}
                          title="Move up"
                          style={{
                            ...indentButtonStyle,
                            opacity: indentTarget ? 1 : 0.35,
                            cursor: indentTarget ? 'pointer' : 'default',
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          onClick={() => moveTask(task, 1)}
                          disabled={!moveDownTarget || busyTaskId === task.id}
                          title="Move down"
                          style={{
                            ...indentButtonStyle,
                            opacity: moveDownTarget ? 1 : 0.35,
                            cursor: moveDownTarget ? 'pointer' : 'default',
                          }}
                        >
                          ▼
                        </button>
                        <button
                          type="button"
                          onClick={() => indent(task)}
                          disabled={!indentTarget || busyTaskId === task.id}
                          title="Indent — make this a sub-task of the row above"
                          style={{
                            ...indentButtonStyle,
                            opacity: indentTarget ? 1 : 0.35,
                            cursor: indentTarget ? 'pointer' : 'default',
                          }}
                        >
                          ⇥
                        </button>
                        <button
                          type="button"
                          onClick={() => outdent(task)}
                          disabled={!task.parentTaskId || busyTaskId === task.id}
                          title="Outdent — promote to a top-level task"
                          style={{
                            ...indentButtonStyle,
                            opacity: task.parentTaskId ? 1 : 0.35,
                            cursor: task.parentTaskId ? 'pointer' : 'default',
                          }}
                        >
                          ⇤
                        </button>
                      </div>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, paddingLeft: depth * 18 }}
                      >
                        <input
                          style={inputStyle}
                          value={cellValue(task, 'title', task.title)}
                          disabled={busyTaskId === task.id}
                          onFocus={() => setDraft({ taskId: task.id, field: 'title', value: task.title || '' })}
                          onChange={(e) => setDraft({ taskId: task.id, field: 'title', value: e.target.value })}
                          onBlur={() => commit(task, 'title', task.title)}
                          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                        />
                        {/* The milestone this task delivers, so the plan shows what a date change is
                            actually moving towards. */}
                        {milestone && (
                          <MilestoneGlyph
                            status={milestone.status}
                            size={13}
                            title={`Delivers: ${milestone.title}`}
                          />
                        )}
                      </div>
                    </div>
                  </td>

                  {['startDate', 'endDate'].map((field) => (
                    <td key={field} style={cellStyle}>
                      <input
                        type="date"
                        style={inputStyle}
                        value={cellValue(task, field, toDateInput(task[field]))}
                        disabled={busyTaskId === task.id}
                        onFocus={() =>
                          setDraft({ taskId: task.id, field, value: toDateInput(task[field]) })
                        }
                        onChange={(e) => setDraft({ taskId: task.id, field, value: e.target.value })}
                        onBlur={() => commit(task, field, toDateInput(task[field]))}
                      />
                    </td>
                  ))}

                  <td style={cellStyle}>
                    <PredecessorCell
                      task={task}
                      tasks={ordered}
                      rowNumber={rowNumber}
                      predecessors={preds}
                      dependencyTypes={dependencyTypes}
                      onWrite={safeWrite}
                    />
                  </td>

                  <td style={cellStyle}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      style={inputStyle}
                      value={cellValue(task, 'percentComplete', String(task.percentComplete ?? 0))}
                      disabled={busyTaskId === task.id}
                      onFocus={() =>
                        setDraft({
                          taskId: task.id,
                          field: 'percentComplete',
                          value: String(task.percentComplete ?? 0),
                        })
                      }
                      onChange={(e) =>
                        setDraft({ taskId: task.id, field: 'percentComplete', value: e.target.value })
                      }
                      onBlur={() => {
                        const value = draft?.value;
                        setDraft(null);
                        const n = Number(value);
                        if (!Number.isFinite(n) || n === (task.percentComplete ?? 0)) return;
                        // Kept in step with status here too, matching the edit popover -- a task at
                        // 100% that still says In Progress is the inconsistency people notice.
                        const clamped = Math.max(0, Math.min(100, Math.round(n)));
                        patch(task.id, {
                          percentComplete: clamped,
                          ...(clamped === 100 && statuses?.includes('Complete')
                            ? { status: 'Complete' }
                            : {}),
                        });
                      }}
                    />
                  </td>

                  <td style={cellStyle}>
                    <select
                      style={{ ...inputStyle, borderColor: 'var(--border)' }}
                      value={task.status || ''}
                      disabled={busyTaskId === task.id}
                      onChange={(e) =>
                        patch(task.id, {
                          status: e.target.value,
                          ...(e.target.value === 'Complete' ? { percentComplete: 100 } : {}),
                          ...(e.target.value === 'Not Started' ? { percentComplete: 0 } : {}),
                        })
                      }
                    >
                      {(statuses || []).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </td>

                  <td style={{ ...cellStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => duplicateTask(task)}
                      disabled={busyTaskId === task.id}
                      title="Duplicate — adds a copy right below, ready to rename"
                      style={{ ...indentButtonStyle, marginRight: 3, padding: '1px 5px', fontSize: 12 }}
                    >
                      ⧉
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTask(task)}
                      disabled={busyTaskId === task.id}
                      title="Delete task"
                      style={{ ...indentButtonStyle, color: 'var(--red)', borderColor: 'var(--red)' }}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}

            {!display.length && (
              <tr>
                <td colSpan={10} style={{ padding: 16, color: 'var(--text-mid)', fontStyle: 'italic' }}>
                  No tasks yet. Populate from SOW, or add one below.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {adding ? (
          <>
            <select
              className="form-input"
              style={{ width: 200, fontSize: 12 }}
              value={newPhaseId}
              onChange={(e) => setNewPhaseId(e.target.value)}
            >
              <option value="">— unassigned —</option>
              {phases.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            <input
              className="form-input"
              style={{ width: 280, fontSize: 12 }}
              placeholder="Task name"
              value={newTitle}
              autoFocus
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTask()}
            />
            <button className="btn btn-primary" onClick={addTask} disabled={!newTitle.trim()}>
              Add
            </button>
            <button className="btn btn-outline" onClick={() => { setAdding(false); setNewTitle(''); }}>
              Cancel
            </button>
          </>
        ) : (
          <button className="btn btn-outline" onClick={() => setAdding(true)}>+ Add task row</button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-mid)' }}>
          Edits save when you leave a cell. Moving a date pushes anything that depends on it.
        </div>
      </div>
    </div>
  );
}

/**
 * The Predecessors cell: existing links as removable chips, plus a picker to add one.
 *
 * Links are labeled by row number (`4 FS`, `7 SS+2`) rather than by task name — MS Project's
 * shorthand, and the only thing that fits a cell this wide once a task has three predecessors.
 * The picker lists names, because choosing by number would mean counting rows.
 *
 * Only tasks *above* this one are offered. That isn't a technical limit — the API's cycle check is
 * the real guard — but a plan sorted into execution order means a backwards link is almost always a
 * misclick, and offering it invites the mistake.
 */
function PredecessorCell({ task, tasks, rowNumber, predecessors, dependencyTypes, onWrite }) {
  const [picking, setPicking] = useState(false);

  const abbreviate = (type) =>
    ({
      'finish-to-start': 'FS',
      'start-to-start': 'SS',
      'finish-to-finish': 'FF',
      'start-to-finish': 'SF',
    })[type] || 'FS';

  const myRow = rowNumber.get(task.id) ?? 0;
  const candidates = tasks.filter(
    (t) => t.id !== task.id && (rowNumber.get(t.id) ?? 0) < myRow &&
      !predecessors.some((d) => d.predecessorId === t.id),
  );

  const add = (predecessorId) =>
    onWrite(() =>
      apiFetch('/task-dependencies', {
        method: 'POST',
        body: JSON.stringify({
          predecessorId,
          successorId: task.id,
          type: dependencyTypes?.[0] || 'finish-to-start',
        }),
      }),
    );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {predecessors.map((d) => (
        <span
          key={d.id}
          title={`Row ${rowNumber.get(d.predecessorId) ?? '?'} · ${d.type}${
            d.lagDays ? ` · ${d.lagDays}d lag` : ''
          } — click to remove`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 11,
            padding: '1px 5px',
            borderRadius: 10,
            background: 'var(--light-bg)',
            border: '1px solid var(--border)',
          }}
        >
          {rowNumber.get(d.predecessorId) ?? '?'} {abbreviate(d.type)}
          {d.lagDays ? `+${d.lagDays}` : ''}
          <button
            onClick={() =>
              onWrite(() => apiFetch(`/task-dependencies/${d.id}`, { method: 'DELETE' }))
            }
            title="Remove this dependency"
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--text-mid)',
              padding: 0,
              fontSize: 12,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </span>
      ))}

      {picking ? (
        <select
          className="form-input"
          style={{ fontSize: 11, padding: '2px 4px', minWidth: 120 }}
          autoFocus
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) add(e.target.value);
            setPicking(false);
          }}
          onBlur={() => setPicking(false)}
        >
          <option value="">Choose a predecessor…</option>
          {candidates.map((t) => (
            <option key={t.id} value={t.id}>
              {rowNumber.get(t.id)}. {t.title}
            </option>
          ))}
        </select>
      ) : (
        <button
          onClick={() => setPicking(true)}
          disabled={!candidates.length}
          title={candidates.length ? 'Add a predecessor' : 'No earlier task to depend on'}
          style={{
            border: '1px dashed var(--border)',
            background: 'transparent',
            cursor: candidates.length ? 'pointer' : 'default',
            color: 'var(--text-mid)',
            borderRadius: 10,
            fontSize: 11,
            padding: '1px 6px',
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

/**
 * Confirmation before wiping a project's schedule.
 *
 * A modal rather than an anchored popover, and dimmed rather than transparent: this is the one
 * destructive action on the page, and it should interrupt rather than sit politely beside a row.
 * It names what will actually go — a count per table — because "are you sure?" with no numbers
 * tells you nothing about what you're about to lose.
 *
 * Cancel is the default focus and the primary-looking button. Getting this wrong costs a
 * re-extraction and a re-promotion, so the safe option is the easy one.
 */
function ConfirmClearSchedule({ projectName, counts, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const lines = [
    ['Phases', counts.phases],
    ['Workstreams', counts.workstreams],
    ['Milestones', counts.milestones],
    ['Tasks', counts.tasks],
    ['Dependencies', counts.dependencies],
  ].filter(([, n]) => n > 0);

  return (
    <>
      <div
        onClick={onCancel}
        style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.35)' }}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="clear-schedule-title"
        className="card"
        style={{
          position: 'fixed',
          zIndex: 51,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 440,
          maxWidth: 'calc(100vw - 24px)',
          marginBottom: 0,
          boxShadow: '0 10px 34px rgba(0,0,0,0.28)',
        }}
      >
        <div id="clear-schedule-title" className="card-title" style={{ color: 'var(--red)' }}>
          Clear this project's schedule?
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-mid)', marginTop: 0 }}>
          This permanently deletes the whole schedule for <strong>{projectName}</strong>:
        </p>

        <ul style={{ fontSize: 13, margin: '0 0 14px', paddingLeft: 20 }}>
          {lines.map(([label, n]) => (
            <li key={label} style={{ marginBottom: 2 }}>
              <strong>{n}</strong> {n === 1 ? label.replace(/s$/, '') : label}
            </li>
          ))}
        </ul>

        {/* The reassuring half. People hesitate here because they're not sure whether "clear"
            also throws away the SOW they'd have to re-upload. It doesn't. */}
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-mid)',
            background: 'var(--light-bg)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px',
            marginBottom: 16,
          }}
        >
          The project and its committed SOW are <strong>not</strong> touched, so you can rebuild the
          schedule immediately with <em>Populate from SOW</em>. Any dates, statuses or percentages
          you edited by hand will be gone.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-primary" onClick={onCancel} autoFocus>
            Cancel
          </button>
          <button
            className="btn btn-outline"
            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
            onClick={onConfirm}
          >
            Yes, clear it
          </button>
        </div>
      </div>
    </>
  );
}

// Exported: CustomerProjectSchedule.js's summary row reuses this rather than a second copy.
export function Stat({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-mid)',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)' }}>{value}</div>
    </div>
  );
}

// ─── ROW / GRID MODEL ────────────────────────────────────────────────────────

/**
 * Flattens the schedule into the ordered rows the chart draws, top to bottom:
 * a phase band, then that phase's task tree (parents before children, indented), then its
 * milestones. Anything with no phase goes into a trailing "Unassigned" group rather than being
 * dropped — a task the AI couldn't place is exactly the thing a PM needs to see.
 */
export function buildPhaseRows(phases, tasks, milestones, collapsedPhases) {
  const rows = [];
  // Milestones drawn on a task's own row rather than a row of their own. Only the leftovers -- a
  // milestone no task delivers -- still get their own line.
  const milestoneByTask = new Map(
    tasks.filter((t) => t.milestoneId).map((t) => [t.id, milestones.find((m) => m.id === t.milestoneId)]),
  );
  const claimedMilestoneIds = new Set(
    [...milestoneByTask.values()].filter(Boolean).map((m) => m.id),
  );

  // `groupColor` is carried down onto every child row so an expanded band can be shaded in a muted
  // version of its own color -- which is what makes a long chart readable as bands rather than as
  // one undifferentiated list of rows.
  const addTaskTree = (phaseId, parentId, depth, groupColor) => {
    tasks
      .filter((t) => (t.phaseId || null) === phaseId && (t.parentTaskId || null) === parentId)
      .forEach((task) => {
        const hasChildren = tasks.some((t) => t.parentTaskId === task.id);
        rows.push({
          kind: 'task',
          id: task.id,
          task,
          depth,
          isSummary: hasChildren,
          milestone: milestoneByTask.get(task.id) || null,
          groupColor,
        });
        addTaskTree(phaseId, task.id, depth + 1, groupColor);
      });
  };

  const addGroup = (key, label, phaseId, colorIndex) => {
    const groupTasks = tasks.filter((t) => (t.phaseId || null) === phaseId);
    const groupMilestones = milestones.filter((m) => (m.phaseId || null) === phaseId);
    // Only unclaimed milestones become rows; the rest ride on their task's line.
    const loneMilestones = groupMilestones.filter((m) => !claimedMilestoneIds.has(m.id));
    if (phaseId === null && !groupTasks.length && !groupMilestones.length) return;

    const childCount = groupTasks.length + loneMilestones.length;
    const isCollapsed = collapsedPhases.has(key) && childCount > 0;

    rows.push({
      kind: 'phase',
      id: key,
      label,
      color: bandColor(colorIndex),
      phase: phaseId ? phases.find((p) => p.id === phaseId) : null,
      collapsible: childCount > 0,
      collapsed: isCollapsed,
      childCount,
      // Same as a collapsed workstream: one band with every child milestone's diamond on it.
      childTasks: groupTasks,
      childMilestones: groupMilestones,
    });

    if (isCollapsed) return;

    const groupColor = bandColor(colorIndex);
    addTaskTree(phaseId, null, 1, groupColor);
    loneMilestones.forEach((m) =>
      rows.push({ kind: 'milestone', id: m.id, milestone: m, depth: 1, groupColor }),
    );
  };

  phases.forEach((p, i) => addGroup(p.id, p.title, p.id, i));
  addGroup('unassigned', 'Unassigned', null, phases.length);
  return rows;
}

/**
 * The same rows, grouped by workstream instead — Judy's slide 9.
 *
 * A workstream row is collapsible. Collapsed, it renders as a single row: one bar spanning its
 * children's whole date range, with every child milestone's diamond drawn on that one line. That's
 * how eighteen monthly payment milestones read as one bar without deleting any of them.
 *
 * `commercial` streams sort above the delivery ones and `ongoing` below, matching the deck's
 * CONTRACT & MOBILIZATION and ONGOING SERVICES bands.
 */
export function buildWorkstreamRows(workstreams, tasks, milestones, collapsed) {
  const KIND_ORDER = { commercial: 0, delivery: 1, ongoing: 2 };
  const ordered = [...workstreams].sort(
    (a, b) =>
      (KIND_ORDER[a.kind] ?? 1) - (KIND_ORDER[b.kind] ?? 1) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name),
  );

  const rows = [];
  const milestoneByTask = new Map(
    tasks.filter((t) => t.milestoneId).map((t) => [t.id, milestones.find((m) => m.id === t.milestoneId)]),
  );
  const claimedMilestoneIds = new Set(
    [...milestoneByTask.values()].filter(Boolean).map((m) => m.id),
  );

  const addGroup = (key, label, workstreamId, kind, colorIndex) => {
    const groupTasks = tasks.filter((t) => (t.workstreamId || null) === workstreamId);
    const groupMilestones = milestones.filter((m) => (m.workstreamId || null) === workstreamId);
    const loneMilestones = groupMilestones.filter((m) => !claimedMilestoneIds.has(m.id));
    if (workstreamId === null && !groupTasks.length && !groupMilestones.length) return;

    const isCollapsed = workstreamId !== null && collapsed.has(workstreamId);
    const childCount = groupTasks.length + loneMilestones.length;

    rows.push({
      kind: 'workstream',
      id: key,
      label,
      workstreamKind: kind,
      color: bandColor(colorIndex),
      collapsible: workstreamId !== null && childCount > 0,
      collapsed: isCollapsed,
      childCount,
      // Everything underneath, so a collapsed row can draw one bar across the lot and put every
      // child milestone's diamond on it.
      childTasks: groupTasks,
      childMilestones: groupMilestones,
    });

    if (isCollapsed) return;

    // Carried onto each child so an expanded stream shades in a muted version of its own color,
    // same as the phase view.
    const groupColor = bandColor(colorIndex);

    // Parents before children within the stream, using the same task hierarchy as the phase view.
    const addTaskTree = (parentId, depth) => {
      groupTasks
        .filter((t) => (t.parentTaskId || null) === parentId)
        .forEach((task) => {
          const hasChildren = groupTasks.some((t) => t.parentTaskId === task.id);
          rows.push({
            kind: 'task',
            id: task.id,
            task,
            depth,
            isSummary: hasChildren,
            milestone: milestoneByTask.get(task.id) || null,
            groupColor,
          });
          addTaskTree(task.id, depth + 1);
        });
    };
    addTaskTree(null, 1);
    loneMilestones.forEach((m) =>
      rows.push({ kind: 'milestone', id: m.id, milestone: m, depth: 1, groupColor }),
    );
  };

  ordered.forEach((w, i) => addGroup(w.id, w.name, w.id, w.kind, i));
  addGroup('unassigned', 'Unassigned', null, 'delivery', ordered.length);
  return rows;
}

/** Earliest and latest date across a collapsed workstream's children. */
export function spanOf(row) {
  const times = [];
  row.childTasks?.forEach((t) => {
    if (t.startDate) times.push(new Date(t.startDate).getTime());
    if (t.endDate) times.push(new Date(t.endDate).getTime());
  });
  row.childMilestones?.forEach((m) => {
    const d = m.actualDate || m.targetDate;
    if (d) times.push(new Date(d).getTime());
  });
  if (!times.length) return null;
  return { start: new Date(Math.min(...times)), end: new Date(Math.max(...times)) };
}

/** Month columns spanning the API-supplied window. Null when nothing is dated. */
export function buildGrid(dateWindow) {
  if (!dateWindow) return null;
  const start = startOfMonth(toDate(dateWindow.start));
  const end = startOfMonth(toDate(dateWindow.end));
  const count = monthsBetween(start, end) + 1;
  const months = Array.from({ length: count }, (_, i) => new Date(start.getFullYear(), start.getMonth() + i, 1));
  return { start, months, count };
}

/** Position of a date as a percentage across the grid. */
export function pct(grid, date) {
  const d = toDate(date);
  if (!d) return null;
  const offset = monthsBetween(grid.start, d) + (d.getDate() - 1) / daysInMonth(d);
  // Clamped: a date outside the window shouldn't render off-canvas. The window is derived from
  // these same dates so this is belt-and-braces, not an expected case.
  return Math.max(0, Math.min(100, (offset / grid.count) * 100));
}

/**
 * `scrollLeft` that puts the today marker in the middle of the visible timeline, or null when there
 * is nothing sensible to center on.
 *
 * The subtlety is that the frozen name column overlays the left LABEL_WIDTH of the viewport, so the
 * timeline is only visible from there rightwards — centering on the full viewport width leaves today
 * sitting half a column left of center.
 *
 * @param todayPct  position of today across the timeline, 0-100, or null when undated.
 */
export function centerScrollLeft(todayPct, scrollWidth, clientWidth) {
  // pct() clamps out-of-window dates to 0/100, so a finished or not-yet-started project would slam
  // to one end and read as a bug. Leave those where they are.
  if (todayPct === null || todayPct <= 0 || todayPct >= 100) return null;
  // An unlaid-out or hidden chart reports clientWidth 0, which would otherwise produce a confident
  // but meaningless offset — centering against a viewport narrower than the frozen column is not a
  // scroll position anyone wants.
  if (!(clientWidth > LABEL_WIDTH) || !(scrollWidth > clientWidth)) return null;
  const todayX = LABEL_WIDTH + ((scrollWidth - LABEL_WIDTH) * todayPct) / 100;
  const visibleTimeline = clientWidth - LABEL_WIDTH;
  // Clamped at both ends: past the end, the browser would clamp anyway, but returning the real
  // maximum keeps this function's output the same as the value that lands on the element.
  return Math.max(0, Math.min(scrollWidth - clientWidth, todayX - LABEL_WIDTH - visibleTimeline / 2));
}

// ─── CHART ───────────────────────────────────────────────────────────────────

// Exported: CustomerProjectSchedule.js reuses this verbatim for the read-only customer view --
// `readOnly` only swaps a "click to edit" tooltip, since selecting a row (highlighting it) isn't
// itself an edit; it's whichever popover a caller renders on selection that is, and the customer
// caller renders none.
export function GanttChart({
  rows,
  grid,
  view,
  dependencies,
  selectedTaskId,
  selectedMilestoneId,
  onSelectTask,
  onSelectMilestone,
  onToggleCollapsed,
  readOnly,
}) {
  // Row index per task, so dependency connectors know which y to draw at.
  const taskRowIndex = new Map();
  rows.forEach((row, i) => {
    if (row.kind === 'task') taskRowIndex.set(row.id, i);
  });

  const today = pct(grid, new Date());
  const scrollRef = useRef(null);

  const headRef = useRef(null);
  usePinnedHeader(scrollRef, headRef);

  // Open on today rather than on the project's first month. A multi-year plan (Clark runs 2023-2027)
  // otherwise starts on history every time, and the interesting part is off-screen to the right.
  //
  // Deliberately keyed on the view and the window, NOT on `rows`: collapsing a band changes rows, and
  // re-centering on every collapse would yank the chart out from under whoever was reading it.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = centerScrollLeft(today, el.scrollWidth, el.clientWidth);
    if (left !== null) el.scrollLeft = left;
  }, [view, grid.count, grid.start, today]);

  return (
    <>
    {/* The chart can be much wider than the viewport on a long project, so it scrolls inside its own
        container rather than making the page scroll sideways. */}
    <div ref={scrollRef} style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: Math.max(720, grid.count * 60) }}>
        {/* MONTH HEADER */}
        <div
          ref={headRef}
          style={{ display: 'flex', borderBottom: '1px solid var(--border)', paddingBottom: 6, position: 'relative', zIndex: 10, background: 'var(--surface)' }}
        >
          {/* Sticky in step with the label column below it, or the month labels would slide out from
              under the frozen names and show through above them. */}
          <div style={{ ...FROZEN_COLUMN, background: 'var(--surface)' }} />
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${grid.count}, 1fr)` }}>
            {grid.months.map((m) => (
              <div
                key={m.toISOString()}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: 'var(--text-mid)',
                  textAlign: 'center',
                  borderLeft: '1px solid var(--border)',
                }}
              >
                {MONTH_LABELS[m.getMonth()]}
                {m.getMonth() === 0 || m === grid.months[0] ? ` '${String(m.getFullYear()).slice(2)}` : ''}
              </div>
            ))}
          </div>
        </div>

        {/* ROWS */}
        <div style={{ display: 'flex', position: 'relative' }}>
          {/* Left: labels, frozen against horizontal scroll (Jeremy, 2026-08-25 — on a four-year plan
              the names were scrolling away long before the bars did). Opaque background and a right
              border, so bars pass behind it rather than showing through. */}
          <div
            style={{
              ...FROZEN_COLUMN,
              background: 'var(--surface)',
              borderRight: '1px solid var(--border)',
            }}
          >
            {rows.map((row) => (
              <div key={`${row.kind}-${row.id}`} style={{ position: 'relative' }}>
                {row.groupColor && <BandTint color={row.groupColor} />}
                <RowLabel
                  row={row}
                  selected={
                    (row.kind === 'task' && row.id === selectedTaskId) ||
                    (row.kind === 'milestone' && row.id === selectedMilestoneId)
                  }
                  onSelect={onSelectTask}
                  onSelectMilestone={onSelectMilestone}
                  onToggleCollapsed={onToggleCollapsed}
                  readOnly={readOnly}
                />
              </div>
            ))}
          </div>

          {/* Right: timeline */}
          <div style={{ flex: 1, position: 'relative' }}>
            {/* Month gridlines behind everything */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                gridTemplateColumns: `repeat(${grid.count}, 1fr)`,
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            >
              {grid.months.map((m) => (
                <div key={m.toISOString()} style={{ borderLeft: '1px solid var(--border)' }} />
              ))}
            </div>

            {/* Today marker */}
            {today !== null && today > 0 && today < 100 && (
              <div
                style={{
                  position: 'absolute',
                  left: `${today}%`,
                  top: 0,
                  bottom: 0,
                  width: 0,
                  borderLeft: '2px dashed var(--yellow)',
                  pointerEvents: 'none',
                  zIndex: 3,
                }}
                aria-hidden="true"
              />
            )}

            <DependencyConnectors
              dependencies={dependencies}
              rows={rows}
              taskRowIndex={taskRowIndex}
              grid={grid}
            />

            {rows.map((row) => (
              <div key={`${row.kind}-${row.id}`} style={{ height: ROW_HEIGHT, position: 'relative' }}>
                {row.groupColor && <BandTint color={row.groupColor} />}
                <RowBar
                  row={row}
                  grid={grid}
                  onSelect={onSelectTask}
                  onSelectMilestone={onSelectMilestone}
                  readOnly={readOnly}
                />
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
    {/* Outside the scroller on purpose (Jeremy, 2026-08-25): in it, the legend scrolled away
        sideways with the bars and dropped below the fold on a tall chart. Out here it can't move
        horizontally at all, and `sticky bottom` keeps it on screen while the chart is. */}
    <Legend sticky />
    </>
  );
}

// `readOnly` (CustomerProjectSchedule.js reuses this component) only changes the "click to edit"
// tooltip wording -- selection/collapse behavior is identical either way, since neither of those
// is itself an edit; it's whichever popover the caller chooses to render on selection that is.
function RowLabel({ row, selected, onSelect, onSelectMilestone, onToggleCollapsed, readOnly }) {
  if (row.kind === 'workstream') {
    return (
      <button
        onClick={() => row.collapsible && onToggleCollapsed(row.id)}
        disabled={!row.collapsible}
        style={{
          height: ROW_HEIGHT,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: row.color,
          background: 'transparent',
          border: 'none',
          borderTop: '1px solid var(--border)',
          textAlign: 'left',
          cursor: row.collapsible ? 'pointer' : 'default',
        }}
        title={
          row.collapsible
            ? `${row.collapsed ? 'Expand' : 'Collapse'} — ${row.childCount} row(s)`
            : row.label
        }
      >
        {row.collapsible && (
          <span style={{ fontSize: 9, width: 8 }}>{row.collapsed ? '▶' : '▼'}</span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label}
        </span>
        {row.collapsed && row.childCount > 0 && (
          <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-light)' }}>
            ({row.childCount})
          </span>
        )}
      </button>
    );
  }

  if (row.kind === 'phase') {
    return (
      <button
        onClick={() => row.collapsible && onToggleCollapsed(row.id)}
        disabled={!row.collapsible}
        style={{
          height: ROW_HEIGHT,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: row.color,
          background: 'transparent',
          border: 'none',
          borderTop: '1px solid var(--border)',
          textAlign: 'left',
          cursor: row.collapsible ? 'pointer' : 'default',
        }}
        title={
          row.collapsible
            ? `${row.collapsed ? 'Expand' : 'Collapse'} — ${row.childCount} row(s)`
            : row.label
        }
      >
        {row.collapsible && (
          <span style={{ fontSize: 9, width: 8 }}>{row.collapsed ? '▶' : '▼'}</span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label}
        </span>
        {row.collapsed && (
          <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-light)' }}>
            ({row.childCount})
          </span>
        )}
      </button>
    );
  }

  if (row.kind === 'milestone') {
    return (
      <button
        onClick={(e) => onSelectMilestone(row.id, e)}
        style={{
          height: ROW_HEIGHT,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingLeft: 12 * row.depth,
          fontSize: 12,
          color: 'var(--text-mid)',
          background: selected ? 'rgba(130,106,175,0.12)' : 'transparent',
          border: 'none',
          borderRadius: 4,
          textAlign: 'left',
          cursor: 'pointer',
        }}
        title={readOnly ? row.milestone.title : `${row.milestone.title} — click to edit`}
      >
        <MilestoneGlyph status={row.milestone.status} size={15} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.milestone.title}
        </span>
        {/* An unassigned milestone is the thing most likely to need attention after a promotion, so
            it says so rather than just looking like any other row. */}
        {!row.milestone.phaseId && (
          <span style={{ fontSize: 9, color: 'var(--yellow)', fontWeight: 700 }}>NO PHASE</span>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={(e) => onSelect(row.id, e)}
      style={{
        height: ROW_HEIGHT,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingLeft: 12 * row.depth,
        fontSize: 12,
        fontWeight: row.isSummary ? 700 : 400,
        color: 'var(--text-dark)',
        background: selected ? 'rgba(47,147,177,0.10)' : 'transparent',
        border: 'none',
        borderRadius: 4,
        textAlign: 'left',
        cursor: 'pointer',
      }}
      title={
        (readOnly ? row.task.title : `${row.task.title} — click to edit`) +
        (row.milestone ? `\n◆ delivers: ${row.milestone.title}` : '')
      }
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.task.title}
      </span>
      {/* Marks the rows whose bar ends in a diamond, so the label column and the chart agree. */}
      {row.milestone && <MilestoneGlyph status={row.milestone.status} size={12} />}
    </button>
  );
}

function RowBar({ row, grid, onSelect, onSelectMilestone, readOnly }) {
  const editHint = readOnly ? '' : '\nClick to edit';
  if (row.kind === 'workstream') {
    const span = spanOf(row);
    if (!span) return null;
    const left = pct(grid, span.start);
    const right = pct(grid, span.end);

    return (
      <>
        <div
          style={{
            position: 'absolute',
            left: `${left}%`,
            width: `${Math.max(right - left, 0.4)}%`,
            top: 12,
            height: 6,
            borderRadius: 3,
            background: row.color,
            opacity: row.collapsed ? 0.6 : 0.35,
          }}
          title={`${row.label}: ${fmtDate(span.start)} → ${fmtDate(span.end)}`}
        />
        {/* Collapsed, every child milestone's diamond lands on this one line — the whole point of
            collapsing: one row in the chart, every row still in the database. */}
        {row.collapsed &&
          row.childMilestones.map((m) => {
            const at = pct(grid, m.actualDate || m.targetDate);
            if (at === null) return null;
            return (
              <button
                key={m.id}
                onClick={(e) => onSelectMilestone(m.id, e)}
                style={{
                  position: 'absolute',
                  left: `${at}%`,
                  top: 3,
                  transform: 'translateX(-50%)',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                title={`${m.title}\nTarget: ${fmtDate(m.targetDate)}${editHint}`}
              >
                <MilestoneGlyph status={m.status} size={14} />
              </button>
            );
          })}
      </>
    );
  }

  if (row.kind === 'phase') {
    const phase = row.phase;
    // A collapsed band without its own dates still gets a bar, derived from its children — otherwise
    // collapsing the Unassigned group makes its contents vanish entirely.
    const span = phase?.startDate && phase?.endDate
      ? { start: phase.startDate, end: phase.endDate }
      : row.collapsed
        ? spanOf(row)
        : null;
    if (!span) return null;

    const left = pct(grid, span.start);
    const right = pct(grid, span.end);
    if (left === null || right === null) return null;

    return (
      <>
        <div
          style={{
            position: 'absolute',
            left: `${left}%`,
            width: `${Math.max(right - left, 0.4)}%`,
            top: 10,
            height: 8,
            borderRadius: 2,
            background: row.color,
            opacity: row.collapsed ? 0.6 : 0.4,
          }}
          title={`${row.label}: ${fmtDate(span.start)} → ${fmtDate(span.end)}`}
        />
        {row.collapsed &&
          row.childMilestones.map((m) => {
            const at = pct(grid, m.actualDate || m.targetDate);
            if (at === null) return null;
            return (
              <button
                key={m.id}
                onClick={(e) => onSelectMilestone(m.id, e)}
                style={{
                  position: 'absolute',
                  left: `${at}%`,
                  top: 3,
                  transform: 'translateX(-50%)',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                title={`${m.title}\nTarget: ${fmtDate(m.targetDate)}${editHint}`}
              >
                <MilestoneGlyph status={m.status} size={14} />
              </button>
            );
          })}
      </>
    );
  }

  if (row.kind === 'milestone') {
    const m = row.milestone;

    // A recurring line (e.g. monthly PM) is one bar from its first to its last period, a tick per
    // period, the completed share filled -- not twelve diamonds.
    if (m.recurrence && m.targetDate && m.recurrenceEnd) {
      const periods = recurrencePeriods(ymd(m.targetDate), ymd(m.recurrenceEnd), 0, m.periodsCompleted || 0);
      const from = pct(grid, m.targetDate);
      const to = pct(grid, m.recurrenceEnd);
      if (periods.length && from !== null && to !== null) {
        const width = Math.max(to - from, 0.4);
        const done = m.periodsCompleted || 0;
        return (
          <button
            onClick={(e) => onSelectMilestone(m.id, e)}
            style={{ position: 'absolute', left: `${from}%`, width: `${width}%`, top: 8, height: 12, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
            title={`${m.title}
${m.recurrence}: ${fmtDate(m.targetDate)} – ${fmtDate(m.recurrenceEnd)}
${done} of ${periods.length} periods done${editHint}`}
          >
            <span style={{ position: 'absolute', inset: 0, borderRadius: 6, border: '1.5px solid var(--violet)', background: 'transparent' }} />
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(done / periods.length) * 100}%`, borderRadius: 6, background: 'var(--violet)' }} />
            {periods.length <= 36 && periods.slice(1).map((p) => (
              <span key={p.index} style={{ position: 'absolute', left: `${((p.index - 1) / periods.length) * 100}%`, top: 2, bottom: 2, width: 1, background: 'var(--bg, #fff)', opacity: 0.8 }} />
            ))}
          </button>
        );
      }
    }

    const at = pct(grid, m.actualDate || m.targetDate);
    if (at === null) return null;
    const slipped = m.actualDate && m.targetDate && new Date(m.actualDate) > new Date(m.targetDate);
    return (
      <button
        onClick={(e) => onSelectMilestone(m.id, e)}
        style={{
          position: 'absolute',
          left: `${at}%`,
          top: 4,
          transform: 'translateX(-50%)',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
        title={
          `${m.title}\nTarget: ${fmtDate(m.targetDate)}\nActual: ${fmtDate(m.actualDate)}` +
          `${m.status ? `\nStatus: ${m.status}` : ''}${editHint}`
        }
      >
        <MilestoneGlyph status={m.status} size={19} color={slipped ? 'var(--red)' : undefined} />
      </button>
    );
  }

  const task = row.task;
  const left = pct(grid, task.startDate);
  const right = pct(grid, task.endDate);
  if (left === null && right === null) return null;

  // A task with only one date still gets a marker rather than vanishing — a bar of zero width is
  // more honest than pretending it isn't scheduled at all.
  const start = left === null ? right : left;
  const width = left === null || right === null ? 0.4 : Math.max(right - left, 0.4);
  const color = TASK_STATUS_COLOR[task.status] || 'var(--primary-light)';

  // The milestone this task delivers rides on the task's own row, its diamond at the bar's end —
  // rather than sitting on a separate line several tasks away (Jeremy, 2026-07-27).
  const milestone = row.milestone;
  const milestoneAt = milestone
    ? pct(grid, milestone.actualDate || milestone.targetDate || task.endDate)
    : null;
  const slipped =
    milestone?.actualDate &&
    milestone?.targetDate &&
    new Date(milestone.actualDate) > new Date(milestone.targetDate);

  return (
    <>
      <button
        onClick={(e) => onSelect(task.id, e)}
        style={{
          position: 'absolute',
          left: `${start}%`,
          width: `${width}%`,
          top: 6,
          height: row.isSummary ? 10 : 16,
          minWidth: 4,
          padding: 0,
          border: 'none',
          borderRadius: row.isSummary ? 2 : 4,
          background: row.isSummary ? 'var(--text-mid)' : 'var(--border)',
          cursor: 'pointer',
          overflow: 'hidden',
          zIndex: 2,
        }}
        title={
          `${task.title}\n${fmtDate(task.startDate)} → ${fmtDate(task.endDate)}\n` +
          `${task.percentComplete}% complete${task.status ? ` · ${task.status}` : ''}` +
          `${milestone ? `\n◆ delivers: ${milestone.title}` : ''}`
        }
      >
        {/* Progress fill inside the bar, the same convention PortfolioTimeline uses. */}
        <span
          style={{
            display: 'block',
            width: `${task.percentComplete || 0}%`,
            height: '100%',
            background: color,
          }}
        />
      </button>

      {milestoneAt !== null && (
        <button
          onClick={(e) => onSelectMilestone(milestone.id, e)}
          style={{
            position: 'absolute',
            left: `${milestoneAt}%`,
            top: 2,
            transform: 'translateX(-50%)',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            lineHeight: 1,
            zIndex: 3,
          }}
          title={
            `${milestone.title}\nTarget: ${fmtDate(milestone.targetDate)}\n` +
            `Actual: ${fmtDate(milestone.actualDate)}${editHint}`
          }
        >
          <MilestoneGlyph
            status={milestone.status}
            size={18}
            color={slipped ? 'var(--red)' : undefined}
          />
        </button>
      )}
    </>
  );
}

/**
 * L-shaped connectors between dependent tasks: out of the predecessor's end, across to the
 * successor's column, then into its start.
 *
 * Drawn as three absolutely-positioned divs rather than SVG because x is a percentage of the window
 * while y is a fixed pixel row height — a single SVG viewBox can't mix those cleanly, and this stays
 * correct when the container resizes.
 *
 * Only drawn when both ends have the dates the arrow needs. A dependency between undated tasks is
 * real data, so it stays visible in the task panel; it just has nowhere to be drawn.
 */
function DependencyConnectors({ dependencies, rows, taskRowIndex, grid }) {
  const taskById = new Map();
  rows.forEach((row) => {
    if (row.kind === 'task') taskById.set(row.id, row.task);
  });

  const centreY = (rowIndex) => rowIndex * ROW_HEIGHT + 14;

  return (
    <>
      {dependencies.map((dep) => {
        const from = taskById.get(dep.predecessorId);
        const to = taskById.get(dep.successorId);
        const fromRow = taskRowIndex.get(dep.predecessorId);
        const toRow = taskRowIndex.get(dep.successorId);
        if (!from || !to || fromRow === undefined || toRow === undefined) return null;

        const fromX = pct(grid, from.endDate ?? from.startDate);
        const toX = pct(grid, to.startDate ?? to.endDate);
        if (fromX === null || toX === null) return null;

        const y1 = centreY(fromRow);
        const y2 = centreY(toRow);
        const midX = Math.max(fromX, toX);
        const style = { position: 'absolute', background: 'var(--text-light)', pointerEvents: 'none', zIndex: 1 };
        const title = `${from.title} → ${to.title} (${dep.type}${dep.lagDays ? `, ${dep.lagDays}d lag` : ''})`;

        return (
          <React.Fragment key={dep.id}>
            {/* out of the predecessor */}
            <div
              style={{ ...style, left: `${fromX}%`, top: y1, width: `${Math.max(midX - fromX, 0)}%`, height: 1 }}
              title={title}
            />
            {/* down/up to the successor's row */}
            <div
              style={{
                ...style,
                left: `${midX}%`,
                top: Math.min(y1, y2),
                height: Math.abs(y2 - y1) || 1,
                width: 1,
              }}
            />
            {/* into the successor */}
            <div
              style={{ ...style, left: `${Math.min(midX, toX)}%`, top: y2, width: `${Math.abs(midX - toX)}%`, height: 1 }}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}

// `sticky` pins it to the bottom of the scrollport (.main-content, which is what actually scrolls
// vertically) so it stays readable over a tall chart, then settles into place at the card's foot.
// It floats over the bars while pinned, hence the opaque background and the shadow to lift it —
// z-index 6 clears the frozen column at 5 and stays under the popovers and modals at 40+.
function Legend({ sticky = false }) {
  const items = [
    // One entry per milestone status, since each has its own shape now -- a legend that only showed
    // "milestone" would leave the shapes unexplained.
    ...['Not Started', 'In Progress', 'Delivered', 'Accepted', 'Billed', 'Paid'].map((status) => ({
      swatch: <MilestoneGlyph status={status} size={13} />,
      label: status,
    })),
    { swatch: <Swatch color="var(--primary-dark)" opacity={0.35} />, label: 'Phase' },
    { swatch: <Swatch color="var(--text-mid)" />, label: 'Workstream (parent task)' },
    { swatch: <Swatch color="var(--primary-light)" />, label: 'In progress' },
    { swatch: <Swatch color="var(--green)" />, label: 'Complete' },
    { swatch: <Swatch color="var(--red)" />, label: 'Blocked / slipped' },
    { swatch: <span style={{ borderLeft: '2px dashed var(--yellow)', paddingLeft: 4 }} />, label: 'Today' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginTop: 16,
        paddingTop: 12,
        borderTop: '1px solid var(--border)',
        fontSize: 11,
        color: 'var(--text-mid)',
        ...(sticky
          ? {
              position: 'sticky',
              bottom: 0,
              zIndex: 6,
              background: 'var(--surface)',
              paddingBottom: 12,
              boxShadow: '0 -6px 10px -8px rgba(0,0,0,0.35)',
            }
          : {}),
      }}
    >
      {items.map((i) => (
        <span key={i.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {i.swatch}
          {i.label}
        </span>
      ))}
    </div>
  );
}

function Swatch({ color, opacity = 1 }) {
  return (
    <span
      style={{ display: 'inline-block', width: 18, height: 8, borderRadius: 2, background: color, opacity }}
      aria-hidden="true"
    />
  );
}

// ─── OUTLINE (no dates yet) ──────────────────────────────────────────────────

// The same rows the chart would draw, as a nested list. Shown when nothing is dated — which is the
// expected state straight after populating from a SOW, since SOWs rarely state real dates.
// Exported: CustomerProjectSchedule.js's fallback for a schedule with nothing dated yet.
export function OutlineView({
  rows,
  selectedTaskId,
  selectedMilestoneId,
  onSelectTask,
  onSelectMilestone,
  onToggleCollapsed,
  readOnly,
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-mid)',
          padding: '8px 12px',
          marginBottom: 12,
          background: 'var(--light-bg)',
          borderRadius: 'var(--radius)',
        }}
      >
        Nothing here has dates yet, so there's no timeline to draw. Add dates — or a project start
        date and duration, then populate from the SOW again — and this becomes a Gantt chart.
      </div>

      {rows.map((row) => {
        if (row.kind === 'workstream') {
          return (
            <button
              key={`workstream-${row.id}`}
              onClick={() => row.collapsible && onToggleCollapsed(row.id)}
              disabled={!row.collapsible}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: row.color || 'var(--heading)',
                marginTop: 14,
                paddingTop: 8,
                borderTop: '1px solid var(--border)',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                cursor: row.collapsible ? 'pointer' : 'default',
              }}
            >
              {row.collapsible && (
                <span style={{ fontSize: 9 }}>{row.collapsed ? '▶' : '▼'}</span>
              )}
              {row.label}
              {row.collapsed && row.childCount > 0 && (
                <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-light)' }}>
                  ({row.childCount})
                </span>
              )}
            </button>
          );
        }

        if (row.kind === 'phase') {
          return (
            <div
              key={`phase-${row.id}`}
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: row.color || 'var(--heading)',
                marginTop: 14,
                paddingTop: 8,
                borderTop: '1px solid var(--border)',
              }}
            >
              {row.label}
            </div>
          );
        }

        if (row.kind === 'milestone') {
          const m = row.milestone;
          return (
            <button
              key={`milestone-${row.id}`}
              onClick={(e) => onSelectMilestone(row.id, e)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '3px 0',
                paddingLeft: 14 * row.depth,
                fontSize: 12,
                color: 'var(--text-mid)',
                background: row.id === selectedMilestoneId ? 'rgba(130,106,175,0.12)' : 'transparent',
                border: 'none',
                borderRadius: 4,
                textAlign: 'left',
                cursor: 'pointer',
              }}
              title={readOnly ? undefined : 'Click to edit'}
            >
              <MilestoneGlyph status={m.status} size={15} />
              <span>{m.title}</span>
              <span style={{ color: 'var(--text-light)', fontSize: 11 }}>
                {m.targetDate ? fmtDate(m.targetDate) : 'no date'}
              </span>
              {!m.phaseId && (
                <span style={{ fontSize: 9, color: 'var(--yellow)', fontWeight: 700 }}>NO PHASE</span>
              )}
            </button>
          );
        }

        const task = row.task;
        return (
          <button
            key={`task-${row.id}`}
            onClick={(e) => onSelectTask(row.id, e)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '3px 0',
              paddingLeft: 14 * row.depth,
              fontSize: 12,
              fontWeight: row.isSummary ? 700 : 400,
              color: 'var(--text-dark)',
              background: row.id === selectedTaskId ? 'rgba(47,147,177,0.10)' : 'transparent',
              border: 'none',
              borderRadius: 4,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span>{task.title}</span>
            {row.milestone && (
              <MilestoneGlyph
                status={row.milestone.status}
                size={12}
                title={`Delivers: ${row.milestone.title}`}
              />
            )}
            {task.percentComplete > 0 && (
              <span style={{ color: 'var(--text-light)', fontSize: 11 }}>
                {task.percentComplete}%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── POPULATE FROM SOW ───────────────────────────────────────────────────────

/**
 * Turns the SOW extraction already stored against this project into a real schedule.
 *
 * Always previews first. The API defaults to a dry run for the same reason this UI does: a promotion
 * writes across three tables at once from partly AI-derived data, and reading the list beforehand is
 * far cheaper than unpicking it afterwards.
 */
function PromoteFromSowPanel({ projectId, onCancel, onApplied }) {
  const [options, setOptions] = useState({
    includeMilestones: true,
    includeWorkstreams: true,
    includePhaseObjectives: false,
    suggestDates: true,
  });
  const [durationMonths, setDurationMonths] = useState('');
  const { templates, error: templatesError } = useProjectTemplates();
  // '' means "the project's own template, else the default" — the API's own fallback, so the picker
  // doesn't have to duplicate that resolution to show a sensible starting value.
  const [templateId, setTemplateId] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const body = (dryRun) => ({
    ...options,
    dryRun,
    ...(durationMonths ? { durationMonths: Number(durationMonths) } : {}),
    ...(options.includePhaseObjectives && templateId ? { templateId } : {}),
  });

  const run = async (dryRun) => {
    setBusy(true);
    setError('');
    try {
      const result = await apiFetch(`/scheduling/promote/${projectId}`, {
        method: 'POST',
        body: JSON.stringify(body(dryRun)),
      });
      if (dryRun) setPreview(result);
      else await onApplied();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
    // The preview describes the previous options, so it's stale the moment one changes.
    setPreview(null);
  };

  return (
    <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--teal)' }}>
      <div className="card-title">Populate from SOW</div>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 14 }}>
        Builds phases, milestones and tasks from this project's committed SOW. Milestones the SOW
        labels with a phase are read directly; the rest are placed by AI, and anything it can't place
        confidently is left unassigned for you rather than guessed at.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <Check label="Milestones" hint="from Milestone Names" checked={options.includeMilestones} onChange={() => toggle('includeMilestones')} />
        <Check label="Workstreams" hint="from Services Included" checked={options.includeWorkstreams} onChange={() => toggle('includeWorkstreams')} />
        <Check label="Fill Gaps from Template" hint="only for phases the SOW doesn't itemize" checked={options.includePhaseObjectives} onChange={() => toggle('includePhaseObjectives')} />
        <Field label="Template" width={220}>
          <select
            className="form-select"
            value={templateId}
            disabled={!options.includePhaseObjectives}
            onChange={(e) => { setTemplateId(e.target.value); setPreview(null); }}
          >
            <option value="">Project default</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Check label="Suggested dates" hint="from start date + duration" checked={options.suggestDates} onChange={() => toggle('suggestDates')} />
        <Field label="Duration override (months)" width={190}>
          <input
            type="number"
            min="1"
            max="120"
            className="form-input"
            placeholder="from the SOW"
            value={durationMonths}
            onChange={(e) => { setDurationMonths(e.target.value); setPreview(null); }}
          />
        </Field>
      </div>

      {(error || templatesError) && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }} role="alert">
          {error || templatesError}
        </div>
      )}

      {preview && <PromotePreview preview={preview} />}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-outline" onClick={() => run(true)} disabled={busy}>
          {busy && !preview ? 'Working…' : preview ? 'Refresh preview' : 'Preview'}
        </button>
        {preview && (
          <button className="btn btn-primary" onClick={() => run(false)} disabled={busy}>
            {busy ? 'Creating…' : `Create ${totalToCreate(preview)} row(s)`}
          </button>
        )}
        <button className="btn btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

// `extra` is for previews that create rows the SOW promotion doesn't — applying a template also
// creates workstreams, and a button that promises fewer rows than land is worse than no count.
function totalToCreate(preview, extra = []) {
  const s = preview.summary || {};
  return ['phases', 'milestones', 'tasks', ...extra].reduce(
    (sum, key) => sum + (s[key]?.toCreate || 0),
    0,
  );
}

function Check({ label, hint, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer', maxWidth: 230 }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <span>
        {label}
        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-light)' }}>{hint}</span>
      </span>
    </label>
  );
}

function PromotePreview({ preview }) {
  const { plan, summary, warnings, durationMonths, startDate } = preview;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
        <PreviewCount label="Phases" counts={summary.phases} />
        <PreviewCount label="Milestones" counts={summary.milestones} />
        <PreviewCount label="Tasks" counts={summary.tasks} />
        <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Dates from
          </div>
          {startDate ? `${fmtDate(startDate)} · ${durationMonths ?? '?'} months` : 'no start date set'}
        </div>
      </div>

      {warnings?.length > 0 && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-mid)',
            borderLeft: '3px solid var(--yellow)',
            paddingLeft: 10,
            marginBottom: 12,
          }}
        >
          {warnings.map((w, i) => (
            <div key={i} style={{ marginBottom: 4 }}>{w}</div>
          ))}
        </div>
      )}

      <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10 }}>
        <PreviewSection title="Phases" rows={plan.phases.map((p) => ({
          key: p.title,
          text: p.title,
          meta: p.startDate ? `${fmtDate(p.startDate)} → ${fmtDate(p.endDate)}` : 'no dates',
          exists: p.exists,
        }))} />
        <PreviewSection title="Milestones" rows={plan.milestones.map((m) => ({
          key: m.title,
          text: m.title,
          meta: `${m.phaseTitle || 'unassigned'} · ${m.phaseSource}${m.targetDate ? ` · ${fmtDate(m.targetDate)}` : ''}`,
          exists: m.exists,
          // The AI-placed ones are what a reviewer should look at hardest.
          highlight: m.phaseSource === 'AI' || m.phaseSource === 'unassigned',
        }))} />
        <PreviewSection title="Workstreams" rows={plan.workstreams.map((w) => ({
          key: w.title, text: w.title, meta: 'spans phases', exists: w.exists,
        }))} />
        <PreviewSection title="Standard phase tasks" rows={plan.objectiveTasks.map((t) => ({
          key: `${t.phaseTitle}-${t.title}`, text: t.title, meta: t.phaseTitle, exists: t.exists,
        }))} />
      </div>
    </div>
  );
}

function PreviewCount({ label, counts }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--heading)' }}>
        {counts.toCreate}
        {counts.alreadyPresent > 0 && (
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-light)' }}>
            {' '}+{counts.alreadyPresent} already there
          </span>
        )}
      </div>
    </div>
  );
}

function PreviewSection({ title, rows }) {
  if (!rows.length) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 4 }}>
        {title}
      </div>
      {rows.map((row) => (
        <div
          key={row.key}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            fontSize: 12,
            padding: '2px 0',
            opacity: row.exists ? 0.45 : 1,
          }}
        >
          <span style={{ color: row.exists ? 'var(--text-light)' : 'var(--green)' }}>
            {row.exists ? '−' : '+'}
          </span>
          <span style={{ color: 'var(--text-dark)' }}>{row.text}</span>
          <span style={{ color: row.highlight ? 'var(--violet)' : 'var(--text-light)', fontSize: 11 }}>
            {row.meta}
          </span>
          {row.exists && <span style={{ fontSize: 10, color: 'var(--text-light)' }}>already there</span>}
        </div>
      ))}
    </div>
  );
}

// ─── CREATE FROM TEMPLATE ────────────────────────────────────────────────────

/**
 * The template library, for the two pickers on this page.
 *
 * Active templates only: a retired methodology stays readable on the projects built from it, but it
 * isn't something you'd start a new schedule with.
 */
function useProjectTemplates() {
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/project-templates')
      .then((rows) => {
        if (!cancelled) setTemplates(Array.isArray(rows) ? rows : []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { templates, error, loading };
}

/**
 * Builds the whole schedule from a methodology template, for a project with no SOW to promote — or
 * one whose SOW lists what gets paid for but not the work behind it.
 *
 * Previews first, like Populate from SOW and for the same reason: it writes across four tables at
 * once, and reading the list is cheaper than unpicking it.
 */
function CreateFromTemplatePanel({ projectId, onCancel, onApplied }) {
  const { templates, error: templatesError, loading } = useProjectTemplates();
  const [templateId, setTemplateId] = useState('');
  const [suggestDates, setSuggestDates] = useState(true);
  const [durationMonths, setDurationMonths] = useState('');
  // Phase ids the PM has unticked. Conditional phases start unticked once a preview names them —
  // that's the whole point of the flag (Software Development, historically). Seeded once per
  // template, tracked separately from `preview`: toggling a phase clears the preview, so keying the
  // seed off `preview === null` would re-exclude the phase the PM had just ticked back on.
  const [excluded, setExcluded] = useState(() => new Set());
  const [seededFor, setSeededFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!templateId && templates.length) {
      setTemplateId((templates.find((t) => t.isDefault) || templates[0]).id);
    }
  }, [templates, templateId]);

  const run = async (dryRun) => {
    setBusy(true);
    setError('');
    try {
      const result = await apiFetch(`/scheduling/apply-template/${projectId}`, {
        method: 'POST',
        body: JSON.stringify({
          dryRun,
          suggestDates,
          ...(templateId ? { templateId } : {}),
          ...(durationMonths ? { durationMonths: Number(durationMonths) } : {}),
          ...(excluded.size ? { excludePhaseIds: [...excluded] } : {}),
        }),
      });
      if (dryRun) {
        setPreview(result);
        // First preview of this template: honor its own conditional flags, then leave the choice
        // alone — every later preview is the PM's selection, not the template's default.
        if (seededFor !== result.plan.template.id) {
          setSeededFor(result.plan.template.id);
          setExcluded(new Set(result.plan.phases.filter((p) => p.conditional).map((p) => p.id)));
        }
      } else {
        await onApplied();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePhase = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // The preview describes the previous selection, so it's stale the moment one changes.
    setPreview(null);
  };

  const includedPhases = (preview?.plan.phases || []).filter((p) => !excluded.has(p.id));

  return (
    <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--teal)' }}>
      <div className="card-title">Create from Template</div>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 14 }}>
        Builds phases, workstreams, tasks and sign-off milestones from a methodology template, with
        no SOW involved. Dates are laid across the project window using each phase's weight; edit
        them afterwards like any other schedule.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <Field label="Template" width={260}>
          <select
            className="form-select"
            value={templateId}
            disabled={loading || !templates.length}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setExcluded(new Set());
              setSeededFor(null);
              setPreview(null);
            }}
          >
            {!templates.length && <option value="">{loading ? 'Loading…' : 'No templates'}</option>}
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Check
          label="Suggested dates"
          hint="from start date + duration"
          checked={suggestDates}
          onChange={() => {
            setSuggestDates((v) => !v);
            setPreview(null);
          }}
        />
        <Field label="Duration override (months)" width={190}>
          <input
            type="number"
            min="1"
            max="120"
            className="form-input"
            placeholder="from the SOW or end date"
            value={durationMonths}
            onChange={(e) => {
              setDurationMonths(e.target.value);
              setPreview(null);
            }}
          />
        </Field>
      </div>

      {(error || templatesError) && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }} role="alert">
          {error || templatesError}
        </div>
      )}

      {preview && (
        <>
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-mid)',
                marginBottom: 6,
              }}
            >
              Phases to create
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              {preview.plan.phases.map((p) => (
                <Check
                  key={p.id}
                  label={p.title}
                  hint={
                    p.conditional
                      ? 'conditional — only some projects'
                      : p.startDate
                        ? `${fmtDate(p.startDate)} → ${fmtDate(p.endDate)}`
                        : 'no dates'
                  }
                  checked={!excluded.has(p.id)}
                  onChange={() => togglePhase(p.id)}
                />
              ))}
            </div>
          </div>
          <TemplatePreview preview={preview} phases={includedPhases} />
        </>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-outline"
          onClick={() => run(true)}
          disabled={busy || !templateId}
        >
          {busy && !preview ? 'Working…' : preview ? 'Refresh preview' : 'Preview'}
        </button>
        {preview && (
          <button className="btn btn-primary" onClick={() => run(false)} disabled={busy}>
            {busy ? 'Creating…' : `Create ${totalToCreate(preview, ['workstreams'])} row(s)`}
          </button>
        )}
        <button className="btn btn-outline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function TemplatePreview({ preview, phases }) {
  const { plan, summary, warnings, durationMonths, startDate } = preview;
  // Rows under an unticked phase aren't going to be created, so they shouldn't be listed as if they
  // were. The server recomputes this on the next preview; this keeps the list honest in between.
  const kept = new Set(phases.map((p) => p.title));
  const inKeptPhase = (r) => !r.phaseTitle || kept.has(r.phaseTitle);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
        <PreviewCount label="Phases" counts={summary.phases} />
        <PreviewCount label="Workstreams" counts={summary.workstreams} />
        <PreviewCount label="Milestones" counts={summary.milestones} />
        <PreviewCount label="Tasks" counts={summary.tasks} />
        <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Dates from
          </div>
          {startDate ? `${fmtDate(startDate)} · ${durationMonths ?? '?'} months` : 'no start date set'}
        </div>
      </div>

      {warnings?.length > 0 && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-mid)',
            borderLeft: '3px solid var(--yellow)',
            paddingLeft: 10,
            marginBottom: 12,
          }}
        >
          {warnings.map((w, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              {w}
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          maxHeight: 300,
          overflowY: 'auto',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: 10,
        }}
      >
        <PreviewSection
          title="Milestones"
          rows={plan.milestones.filter(inKeptPhase).map((m) => ({
            key: m.title,
            text: m.title,
            meta: [m.phaseTitle, m.targetDate ? fmtDate(m.targetDate) : null]
              .filter(Boolean)
              .join(' · '),
            exists: m.exists,
          }))}
        />
        <PreviewSection
          title="Tasks"
          rows={plan.tasks.filter(inKeptPhase).map((t, i) => ({
            key: `${t.title}-${i}`,
            text: t.parentTitle ? `↳ ${t.title}` : t.title,
            meta: [
              t.phaseTitle,
              t.durationDays ? `${t.durationDays}d` : null,
              t.defaultRole,
              t.startDate ? `${fmtDate(t.startDate)} → ${fmtDate(t.endDate)}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
            exists: t.exists,
          }))}
        />
      </div>
    </div>
  );
}

// ─── SCHEDULE SNAPSHOTS ──────────────────────────────────────────────────────

/**
 * Save and review frozen copies of this project's schedule — the schedule-side equivalent of the
 * forecast's baselines.
 *
 * Captured rows are never editable, by definition; the name and notes are, since those are how
 * someone finds the right snapshot later.
 */
function SnapshotsPanel({ projectId, onCancel }) {
  const [snapshots, setSnapshots] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Deleting a snapshot takes its captured lines with it and can't be undone — the schedule it
  // recorded has usually moved on. Same two-step the rest of this page gives destructive actions.
  const [confirmId, setConfirmId] = useState(null);
  // Which saved copy is open for comparison against the live schedule, if any.
  const [compareId, setCompareId] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      setSnapshots(await apiFetch(`/schedule-snapshots?projectId=${projectId}`));
    } catch (e) {
      setError(e.message);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const hasBaselines = snapshots.some((s) => s.isBaseline);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/schedule-snapshots/${projectId}`, {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), notes: notes.trim() || undefined }),
      });
      setName('');
      setNotes('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const open = async (id) => {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      setDetail(await apiFetch(`/schedule-snapshots/${id}`));
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (id) => {
    setConfirmId(null);
    setBusy(true);
    setError('');
    try {
      await apiFetch(`/schedule-snapshots/${id}`, { method: 'DELETE' });
      if (openId === id) {
        setOpenId(null);
        setDetail(null);
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--violet)' }}>
      <div className="card-title">Schedule baselines &amp; snapshots</div>
      <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 14 }}>
        A frozen copy of every phase, milestone and task as they stand right now, kept under a name
        you choose. Neither kind changes afterwards, even when the live schedule does. A{' '}
        <strong>baseline</strong> is one of these taken when you stamp the schedule from the Project
        Launch page — the committed plan that variance is measured against. A{' '}
        <strong>snapshot</strong> is the same capture saved by hand, for your own reference.
      </div>

      {hasBaselines && (
        <div style={{ marginBottom: 16 }}>
          <button
            className="btn btn-outline"
            style={{ padding: '2px 8px', fontSize: 12, marginBottom: 8 }}
            onClick={() => setMatrixOpen((v) => !v)}
          >
            {matrixOpen ? 'Hide' : 'Show'} every baseline side by side
          </button>
          {matrixOpen && <BaselineMatrix projectId={projectId} />}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
        <Field label="Snapshot name" width={240}>
          <input
            className="form-input"
            placeholder="e.g. Contract baseline"
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Notes (optional)" width={360}>
          <input
            className="form-input"
            placeholder="Why this plan is worth keeping"
            value={notes}
            maxLength={4000}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={save} disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save snapshot'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }} role="alert">
          {error}
        </div>
      )}

      {!snapshots.length ? (
        <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
          No snapshots yet for this project.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          {snapshots.map((s, i) => (
            <div
              key={s.id}
              style={{ borderTop: i ? '1px solid var(--border)' : undefined, padding: '8px 10px' }}
            >
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-outline"
                  style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => open(s.id)}
                >
                  {openId === s.id ? 'Hide' : 'View'}
                </button>
                <button
                  className="btn btn-outline"
                  style={{ padding: '2px 8px', fontSize: 12 }}
                  onClick={() => setCompareId(compareId === s.id ? null : s.id)}
                >
                  {compareId === s.id ? 'Hide compare' : 'Compare'}
                </button>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-dark)' }}>
                  {s.name}
                </span>
                {s.isBaseline && (
                  <span className="badge badge-green" style={{ fontSize: 10 }}>
                    Baseline{s.baselineNumber ? ` ${s.baselineNumber}` : ''}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-light)' }}>
                  {fmtDate(s.capturedAt)} ·{' '}
                  {s.capturedBy?.name || s.capturedByName || 'unknown'} · {s.phaseCount} phase(s),{' '}
                  {s.milestoneCount} milestone(s), {s.taskCount} task(s)
                </span>
                {confirmId === s.id ? (
                  <span
                    style={{
                      marginLeft: 'auto',
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: 'var(--red)' }}>Delete permanently?</span>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '2px 8px', fontSize: 12, borderColor: 'var(--red)', color: 'var(--red)' }}
                      onClick={() => remove(s.id)}
                      disabled={busy}
                    >
                      Yes, delete
                    </button>
                    <button
                      className="btn btn-outline"
                      style={{ padding: '2px 8px', fontSize: 12 }}
                      onClick={() => setConfirmId(null)}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    className="btn btn-outline"
                    style={{
                      marginLeft: 'auto',
                      padding: '2px 8px',
                      fontSize: 12,
                      borderColor: 'var(--red)',
                      color: 'var(--red)',
                    }}
                    onClick={() => setConfirmId(s.id)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                )}
              </div>
              {s.notes && (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 4 }}>{s.notes}</div>
              )}
              {openId === s.id && (
                <SnapshotLines detail={detail} />
              )}
              {compareId === s.id && <SnapshotCompare snapshotId={s.id} />}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn btn-outline" onClick={onCancel} disabled={busy}>
          Close
        </button>
      </div>
    </div>
  );
}

// Days off a commitment, as a signed chip. Late is red, early is green — the same reading as the
// Milestone Tracker's Slip column, so the two pages agree at a glance.
function SlipChip({ days, suffix = 'd' }) {
  if (days === null || days === undefined) return <span style={{ color: 'var(--text-light)' }}>—</span>;
  if (days === 0) return <span style={{ color: 'var(--text-light)' }}>on plan</span>;
  return (
    <span style={{ color: days > 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>
      {days > 0 ? '+' : ''}{days}{suffix}
    </span>
  );
}

/**
 * How many times a target date has been edited — and whether that number can be trusted as the whole
 * story. Nothing recorded date edits before the move log shipped (ADR 0047), so a milestone whose
 * original commitment doesn't match where the log first saw it moved an unknown number of times
 * before anyone was counting. Rendering that as a blank would read as "never moved", which is the
 * one thing it definitely isn't.
 */
function MovesCell({ row }) {
  const { moveCount, moveCountIsFloor } = row;
  if (!moveCount && !moveCountIsFloor) return <span style={{ color: 'var(--text-light)' }}>—</span>;
  const unknownBefore = 'It also moved before move logging existed, so the real count is higher — '
    + 'those edits were never recorded and can\u2019t be recovered.';
  return (
    <span
      className={moveCount > 2 ? 'badge badge-yellow' : 'badge badge-blue'}
      style={{ fontSize: 10 }}
      title={moveCountIsFloor
        ? `${moveCount} logged edit(s). ${unknownBefore}`
        : `${moveCount} edit(s) to the target date.`}
    >
      {moveCount}{moveCountIsFloor ? '+' : '\u00d7'}
    </span>
  );
}

const COMPARE_STATE_STYLE = {
  moved: { label: 'Moved', className: 'badge badge-yellow' },
  added: { label: 'Added since', className: 'badge badge-blue' },
  removed: { label: 'Removed since', className: 'badge' },
  unchanged: { label: 'Unchanged', className: 'badge badge-green' },
};

/**
 * One saved copy against the live schedule. Milestones first, since that is what a baseline is
 * normally read for; phases and tasks are a row-kind away.
 */
function SnapshotCompare({ snapshotId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rowType, setRowType] = useState('milestone');
  const [hideUnchanged, setHideUnchanged] = useState(true);

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    apiFetch(`/schedule-snapshots/${snapshotId}/compare?rowType=${rowType}`)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [snapshotId, rowType]);

  if (error) return <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8 }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 8 }}>Loading…</div>;

  const isMilestone = rowType === 'milestone';
  const rows = (hideUnchanged ? data.rows.filter((r) => r.state !== 'unchanged') : data.rows)
    .slice()
    .sort((a, b) => (b.slipDays ?? -Infinity) - (a.slipDays ?? -Infinity));
  const was = (r) => (isMilestone ? r.baseline?.targetDate : r.baseline?.endDate);
  const now = (r) => (isMilestone ? r.live?.targetDate : r.live?.endDate);

  return (
    <div style={{ marginTop: 10, background: 'var(--bg-soft, #fafafa)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: 12 }}>
        <select className="form-select" style={{ width: 130, fontSize: 12, padding: '2px 6px' }}
          value={rowType} onChange={(e) => setRowType(e.target.value)}
        >
          <option value="milestone">Milestones</option>
          <option value="phase">Phases</option>
          <option value="task">Tasks</option>
        </select>
        <label style={{ display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-mid)' }}>
          <input type="checkbox" checked={hideUnchanged} onChange={(e) => setHideUnchanged(e.target.checked)} />
          Only what changed
        </label>
        <span style={{ color: 'var(--text-mid)' }}>
          {data.summary.moved} moved · {data.summary.added} added · {data.summary.removed} removed ·{' '}
          {data.summary.unchanged} unchanged
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--text-mid)' }}>
          Net <SlipChip days={data.summary.netSlipDays} /> · worst{' '}
          <SlipChip days={data.summary.worstSlipDays} />
        </span>
      </div>

      {!rows.length ? (
        <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
          Nothing has changed here since this {data.snapshot.isBaseline ? 'baseline' : 'snapshot'}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-mid)' }}>
                <th style={{ padding: '4px 6px' }}>{isMilestone ? 'Milestone' : rowType === 'phase' ? 'Phase' : 'Task'}</th>
                <th style={{ padding: '4px 6px' }}>Phase</th>
                <th style={{ padding: '4px 6px' }}>Then</th>
                <th style={{ padding: '4px 6px' }}>Now</th>
                <th style={{ padding: '4px 6px' }} title="Days the planned date has moved since this capture">Moved</th>
                {isMilestone && (
                  <th style={{ padding: '4px 6px' }} title="Days between the captured commitment and where the milestone actually landed">
                    Landed
                  </th>
                )}
                <th style={{ padding: '4px 6px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = COMPARE_STATE_STYLE[r.state];
                return (
                  <tr key={`${r.sourceId}-${r.state}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 6px' }}>
                      {r.title}
                      {r.baselineTitle && r.baselineTitle !== r.title && (
                        <span style={{ color: 'var(--text-light)' }}> (was “{r.baselineTitle}”)</span>
                      )}
                    </td>
                    <td style={{ padding: '4px 6px', color: 'var(--text-mid)' }}>{r.phaseTitle || '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{was(r) ? fmtDate(was(r)) : '—'}</td>
                    <td style={{ padding: '4px 6px' }}>{now(r) ? fmtDate(now(r)) : '—'}</td>
                    <td style={{ padding: '4px 6px' }}><SlipChip days={r.slipDays} /></td>
                    {isMilestone && (
                      <td style={{ padding: '4px 6px' }}><SlipChip days={r.actualSlipDays} /></td>
                    )}
                    <td style={{ padding: '4px 6px' }}>
                      <span className={badge.className} style={{ fontSize: 10 }}>{badge.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Every baseline this project has set, as one milestone-by-baseline grid — the read for "this date
 * has moved four times, and here is which commitment each move was against."
 */
function BaselineMatrix({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [movedOnly, setMovedOnly] = useState(false);

  useEffect(() => {
    let live = true;
    apiFetch(`/schedule-snapshots/baseline-matrix?projectId=${projectId}`)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [projectId]);

  if (error) return <div style={{ fontSize: 12, color: 'var(--red)' }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 12, color: 'var(--text-light)' }}>Loading…</div>;
  if (!data.baselines.length) {
    return <div style={{ fontSize: 12, color: 'var(--text-light)' }}>No baselines set yet.</div>;
  }

  const rows = movedOnly
    ? data.rows.filter((r) => r.moveCount > 0 || r.moveCountIsFloor || r.slipFromFirst)
    : data.rows;
  const cell = { padding: '4px 6px', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
        <label style={{ display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-mid)' }}>
          <input type="checkbox" checked={movedOnly} onChange={(e) => setMovedOnly(e.target.checked)} />
          Only milestones that have moved
        </label>
        <span style={{ color: 'var(--text-light)' }}>
          <strong>Target vs</strong> columns measure the plan moving; <strong>Actual vs</strong>{' '}
          columns measure delivery, and stay blank until the milestone is delivered. “Moves” counts
          every hand edit to the target date, not just the ones that earned a re-baseline — a{' '}
          <strong>+</strong> means it moved before move logging existed, so the real number is higher
          than the one shown.
        </span>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-mid)', background: 'var(--bg-soft, #fafafa)' }}>
              <th style={cell}>Milestone</th>
              {data.baselines.map((b) => (
                <th key={b.id} style={cell} title={`${fmtDate(b.capturedAt)} · ${b.capturedByName || 'unknown'}${b.notes ? ` · ${b.notes}` : ''}`}>
                  {b.name}
                </th>
              ))}
              <th style={cell}>Current target</th>
              <th style={cell} title="How far the PLAN has moved: the first baseline that carried this milestone, to its current target date. Says nothing about delivery.">
                Target vs first
              </th>
              <th style={cell} title="How far the plan has moved since the most recent baseline, to the current target date.">
                Target vs latest
              </th>
              <th style={cell}>Actual</th>
              <th style={cell} title="Whether it was DELIVERED late against the plan as it stands now. Blank until it is delivered.">
                Actual vs target
              </th>
              <th style={cell} title="Whether it was delivered late against what was originally committed to — the question a customer asks. Blank until it is delivered, or if no baseline ever carried it.">
                Actual vs first
              </th>
              <th style={cell}>Moves</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={cell}>
                  {r.title}
                  {r.phaseTitle && <span style={{ color: 'var(--text-light)' }}> · {r.phaseTitle}</span>}
                </td>
                {r.dates.map((d, i) => (
                  <td key={data.baselines[i].id} style={{ ...cell, color: d ? undefined : 'var(--text-light)' }}>
                    {d ? fmtDate(d) : '—'}
                  </td>
                ))}
                <td style={cell}>{r.targetDate ? fmtDate(r.targetDate) : '—'}</td>
                <td style={cell}><SlipChip days={r.slipFromFirst} /></td>
                <td style={cell}><SlipChip days={r.slipFromLatest} /></td>
                <td style={cell}>{r.actualDate ? fmtDate(r.actualDate) : '—'}</td>
                <td style={cell}><SlipChip days={r.deliveryVsTarget} /></td>
                <td style={cell}><SlipChip days={r.deliveryVsFirst} /></td>
                <td style={cell}><MovesCell row={r} /></td>
              </tr>
            ))}
            {data.removed.map((r) => (
              // Descoped work, which ADR 0033 deletes from the live plan — the baseline is the only
              // place it still exists.
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)', color: 'var(--text-light)' }}>
                <td style={cell}>
                  <s>{r.title}</s> <span className="badge" style={{ fontSize: 10 }}>Removed since</span>
                </td>
                {r.dates.map((d, i) => (
                  <td key={data.baselines[i].id} style={cell}>{d ? fmtDate(d) : '—'}</td>
                ))}
                <td style={cell} colSpan={7}>No longer on the schedule</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SnapshotLines({ detail }) {
  if (!detail) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 8 }}>Loading…</div>
    );
  }
  const group = (rowType) => detail.lines.filter((l) => l.rowType === rowType);
  const dates = (l) =>
    l.rowType === 'milestone'
      ? l.targetDate
        ? fmtDate(l.targetDate)
        : 'no date'
      : l.startDate
        ? `${fmtDate(l.startDate)} → ${fmtDate(l.endDate)}`
        : 'no dates';

  return (
    <div
      style={{
        marginTop: 8,
        maxHeight: 300,
        overflowY: 'auto',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: 10,
      }}
    >
      {[
        ['Phases', 'phase'],
        ['Milestones', 'milestone'],
        ['Tasks', 'task'],
      ].map(([title, rowType]) => (
        <PreviewSection
          key={rowType}
          title={title}
          rows={group(rowType).map((l) => ({
            key: l.id,
            text: l.parentTitle ? `↳ ${l.title}` : l.title,
            meta: [
              l.phaseTitle,
              dates(l),
              l.status,
              l.percentComplete ? `${l.percentComplete}%` : null,
            ]
              .filter(Boolean)
              .join(' · '),
            exists: false,
          }))}
        />
      ))}
    </div>
  );
}

// ─── FORMS ───────────────────────────────────────────────────────────────────
// Each is a small card that appears above the chart. Deliberately plain forms rather than modals:
// laying out a schedule means adding several rows in a row, and a modal that has to be reopened
// each time gets old fast.

function FormCard({ title, children, onCancel, onSubmit, submitLabel, canSubmit }) {
  return (
    <form
      className="card"
      style={{ marginBottom: 20, borderLeft: '4px solid var(--primary-light)' }}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="card-title">{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>{children}</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
          {submitLabel}
        </button>
        <button className="btn btn-outline" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, width = 220 }) {
  return (
    <div className="form-group" style={{ marginBottom: 0, width }}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function AddPhaseForm({ projectId, allPhases, existingTitles, onCancel, onSubmit }) {
  // Only phases the project doesn't already have: @@unique([projectId, title]) means offering a
  // duplicate would just produce a 409.
  const available = allPhases.filter((p) => !existingTitles.includes(p.title));
  const [title, setTitle] = useState(available[0]?.title || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const selected = allPhases.find((p) => p.title === title);

  if (!available.length) {
    return (
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
          All six canonical phases already exist on this project.
        </div>
        <button className="btn btn-outline" style={{ marginTop: 12 }} onClick={onCancel}>Close</button>
      </div>
    );
  }

  return (
    <FormCard
      title="Add phase"
      onCancel={onCancel}
      canSubmit={!!title}
      submitLabel="Add phase"
      onSubmit={() =>
        onSubmit({
          projectId,
          title,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        })
      }
    >
      <Field label="Phase" width={280}>
        <select className="form-input" value={title} onChange={(e) => setTitle(e.target.value)}>
          {available.map((p) => (
            <option key={p.title} value={p.title}>
              {p.sequence}. {p.title}
              {p.conditional ? ' (conditional)' : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Start">
        <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </Field>
      <Field label="End">
        <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </Field>
      {selected?.deliverables?.length > 0 && (
        <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text-mid)' }}>
          <strong style={{ color: 'var(--text-dark)' }}>Standard deliverables:</strong>{' '}
          {selected.deliverables.join(' · ')}
        </div>
      )}
    </FormCard>
  );
}

function AddTaskForm({ projectId, phases, tasks, milestones, statuses, onCancel, onSubmit }) {
  const [title, setTitle] = useState('');
  const [phaseId, setPhaseId] = useState(phases[0]?.id || '');
  const [parentTaskId, setParentTaskId] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState(statuses[0]);

  // Only tasks in the chosen phase can be the parent — the API rejects a cross-phase parent anyway,
  // but offering it would be an invitation to a 400.
  const parentOptions = tasks.filter((t) => (t.phaseId || '') === phaseId);

  return (
    <FormCard
      title="Add task"
      onCancel={onCancel}
      canSubmit={title.trim().length > 0}
      submitLabel="Add task"
      onSubmit={() =>
        onSubmit({
          projectId,
          title: title.trim(),
          ...(phaseId ? { phaseId } : {}),
          ...(parentTaskId ? { parentTaskId } : {}),
          ...(milestoneId ? { milestoneId } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
          status,
        })
      }
    >
      <Field label="Title" width={320}>
        <input
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Data Conversion 1"
        />
      </Field>
      <Field label="Phase">
        <select
          className="form-input"
          value={phaseId}
          onChange={(e) => {
            setPhaseId(e.target.value);
            setParentTaskId('');
          }}
        >
          {phases.map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Parent (workstream)">
        <select className="form-input" value={parentTaskId} onChange={(e) => setParentTaskId(e.target.value)}>
          <option value="">— none —</option>
          {parentOptions.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Delivers milestone">
        <select className="form-input" value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)}>
          <option value="">— none —</option>
          {milestones.map((m) => (
            <option key={m.id} value={m.id}>{m.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Start">
        <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </Field>
      <Field label="End">
        <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </Field>
      <Field label="Status">
        <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>
    </FormCard>
  );
}

function AddMilestoneForm({ projectId, phases, statuses, onCancel, onSubmit }) {
  const [title, setTitle] = useState('');
  const [phaseId, setPhaseId] = useState(phases[0]?.id || '');
  const [targetDate, setTargetDate] = useState('');
  const [status, setStatus] = useState(statuses[0]);
  const [acceptanceCriteria, setAcceptanceCriteria] = useState('');

  return (
    <FormCard
      title="Add milestone"
      onCancel={onCancel}
      canSubmit={title.trim().length > 0}
      submitLabel="Add milestone"
      onSubmit={() =>
        onSubmit({
          projectId,
          title: title.trim(),
          ...(phaseId ? { phaseId } : {}),
          ...(targetDate ? { targetDate } : {}),
          ...(acceptanceCriteria.trim() ? { acceptanceCriteria: acceptanceCriteria.trim() } : {}),
          status,
        })
      }
    >
      <Field label="Title" width={320}>
        <input
          className="form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Go-Live System Cutover"
        />
      </Field>
      <Field label="Phase">
        <select className="form-input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
          {phases.map((p) => (
            <option key={p.id} value={p.id}>{p.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Target date">
        <input type="date" className="form-input" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
      </Field>
      <Field label="Status">
        <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>
      <Field label="Acceptance criteria" width="100%">
        <textarea
          className="form-textarea"
          rows={2}
          value={acceptanceCriteria}
          onChange={(e) => setAcceptanceCriteria(e.target.value)}
          placeholder="The stated conditions for accepting this deliverable"
        />
      </Field>
    </FormCard>
  );
}

function AddDependencyForm({ tasks, types, onCancel, onSubmit }) {
  const [predecessorId, setPredecessorId] = useState(tasks[0]?.id || '');
  const [successorId, setSuccessorId] = useState(tasks[1]?.id || '');
  const [type, setType] = useState(types[0]);
  const [lagDays, setLagDays] = useState('0');

  const sameTask = predecessorId === successorId;

  return (
    <FormCard
      title="Add dependency"
      onCancel={onCancel}
      canSubmit={!sameTask && !!predecessorId && !!successorId}
      submitLabel="Link tasks"
      onSubmit={() =>
        onSubmit({ predecessorId, successorId, type, lagDays: Number(lagDays) || 0 })
      }
    >
      <Field label="Predecessor" width={280}>
        <select className="form-input" value={predecessorId} onChange={(e) => setPredecessorId(e.target.value)}>
          {tasks.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Successor" width={280}>
        <select className="form-input" value={successorId} onChange={(e) => setSuccessorId(e.target.value)}>
          {tasks.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
      </Field>
      <Field label="Type">
        <select className="form-input" value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </Field>
      <Field label="Lag (days)" width={140}>
        <input
          type="number"
          className="form-input"
          value={lagDays}
          onChange={(e) => setLagDays(e.target.value)}
        />
      </Field>
      {sameTask && (
        <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--red)' }}>
          A task can't depend on itself — pick two different tasks.
        </div>
      )}
    </FormCard>
  );
}

// ─── MILESTONE EDIT PANEL ────────────────────────────────────────────────────

/**
 * Edit a milestone — including assigning it to a phase, which is what a promoted-but-unassigned
 * milestone needs and what there was previously no way to do at all.
 *
 * Phase and workstream are independent: a milestone can sit in one, both or neither. Clearing either
 * is a real edit ("this doesn't belong to a phase after all"), which is why the API takes explicit
 * `clearPhase`/`clearWorkstream` flags rather than a null id — `@IsUUID` would reject null.
 */
function EditMilestonePanel({
  milestone,
  phases,
  workstreams,
  statuses,
  deliveringTask,
  onClose,
  onSave,
  onDelete,
}) {
  const [phaseId, setPhaseId] = useState(milestone.phaseId || '');
  const [workstreamId, setWorkstreamId] = useState(milestone.workstreamId || '');
  const [targetDate, setTargetDate] = useState(toDateInput(milestone.targetDate));
  const [actualDate, setActualDate] = useState(toDateInput(milestone.actualDate));
  const [status, setStatus] = useState(milestone.status || statuses[0]);
  const [isGoLive, setIsGoLive] = useState(!!milestone.isGoLive);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // A milestone has no percentage of its own — a milestone is a moment, not work. What it does have
  // is the task that delivers it, and that task's percentage is what the Gantt bar shows. So
  // marking a milestone delivered or accepted completes the task too (Jeremy, 2026-07-28); leaving
  // a 40%-complete bar behind a signed-off milestone is the inconsistency worth removing.
  const completesDeliveringTask =
    !!deliveringTask &&
    MILESTONE_COMPLETE.has(status) &&
    (deliveringTask.status !== 'Complete' || deliveringTask.percentComplete !== 100);

  // Setting an actual date is the other way people say "this happened", so it implies Delivered
  // unless a more specific terminal status is already set.
  const handleActualDateChange = (value) => {
    setActualDate(value);
    if (value && !MILESTONE_COMPLETE.has(status) && statuses.includes('Delivered')) {
      setStatus('Delivered');
    }
  };

  const save = () =>
    onSave(
      {
        ...(phaseId ? { phaseId } : milestone.phaseId ? { clearPhase: true } : {}),
        ...(workstreamId
          ? { workstreamId }
          : milestone.workstreamId
            ? { clearWorkstream: true }
            : {}),
        ...(targetDate ? { targetDate } : {}),
        ...(actualDate ? { actualDate } : {}),
        status,
        isGoLive,
      },
      // Second argument, applied to the delivering task rather than the milestone.
      completesDeliveringTask ? { status: 'Complete', percentComplete: 100 } : null,
    );

  return (
    <div className="card" style={{ marginTop: 20, borderLeft: '4px solid var(--violet)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>
            ◆ {milestone.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {milestone.inferredPhase ? (
              <>
                Extraction attributed this to <strong>{milestone.inferredPhase}</strong>
                {milestone.inferredPhase !== phases.find((p) => p.id === phaseId)?.title && (
                  <span style={{ color: 'var(--text-light)' }}> — corrected below</span>
                )}
              </>
            ) : (
              'No phase recorded by the extraction'
            )}
          </div>
        </div>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
        <Field label="Phase" width={230}>
          <select className="form-input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            <option value="">— unassigned —</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Workstream" width={230}>
          <select
            className="form-input"
            value={workstreamId}
            onChange={(e) => setWorkstreamId(e.target.value)}
          >
            <option value="">— none —</option>
            {workstreams.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Target date">
          <input type="date" className="form-input" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
        </Field>
        <Field label="Actual date">
          <input type="date" className="form-input" value={actualDate} onChange={(e) => handleActualDateChange(e.target.value)} />
        </Field>
        <Field label="Status">
          <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
      </div>

      {/* What the Portfolio Timeline anchors this project's bar and ◆ marker to. The initial values
          came from a title heuristic (packages/db/scripts/flag-go-live-milestones.js), so being able
          to correct one here is the point — "Go-Live Authorization" is a gate, not the event. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 12, color: 'var(--text-mid)' }}>
        <input type="checkbox" checked={isGoLive} onChange={(e) => setIsGoLive(e.target.checked)} />
        This milestone is the <strong>go-live</strong> — use its date on the Portfolio Timeline
      </label>

      {/* Says what saving will do beyond this milestone, before it happens. A silent write to
          another row is the kind of thing that erodes trust in the whole page. */}
      {completesDeliveringTask && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--text-mid)',
            background: 'var(--light-bg)',
            borderRadius: 'var(--radius)',
            padding: '8px 10px',
          }}
        >
          Saving will also mark <strong>{deliveringTask.title}</strong> complete at 100%.
        </div>
      )}

      {milestone.acceptanceCriteria && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-mid)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            Acceptance criteria
          </div>
          {milestone.acceptanceCriteria}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={save}>Save changes</button>
        {!confirmingDelete ? (
          <button className="btn btn-outline" onClick={() => setConfirmingDelete(true)}>
            Delete milestone
          </button>
        ) : (
          <>
            <button
              className="btn"
              style={{ background: 'var(--red)', color: 'var(--white)' }}
              onClick={onDelete}
            >
              Confirm delete
            </button>
            <button className="btn btn-outline" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── TASK EDIT PANEL ─────────────────────────────────────────────────────────

function EditTaskPanel({
  task,
  phases,
  workstreams,
  statuses,
  dependencies,
  tasks,
  onClose,
  onSave,
  onDelete,
  onDeleteDependency,
}) {
  const [startDate, setStartDate] = useState(toDateInput(task.startDate));
  const [endDate, setEndDate] = useState(toDateInput(task.endDate));
  const [percentComplete, setPercentComplete] = useState(String(task.percentComplete ?? 0));
  const [status, setStatus] = useState(task.status || statuses[0]);
  const [phaseId, setPhaseId] = useState(task.phaseId || '');
  const [workstreamId, setWorkstreamId] = useState(task.workstreamId || '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const titleOf = (id) => tasks.find((t) => t.id === id)?.title || 'unknown task';
  const incoming = dependencies.filter((d) => d.successorId === task.id);
  const outgoing = dependencies.filter((d) => d.predecessorId === task.id);
  const phase = phases.find((p) => p.id === task.phaseId);

  /**
   * Status and % complete are two ways of saying the same thing, so they're kept in step here
   * (Jeremy, 2026-07-28). Marking a task Complete and leaving it at 40% is never what was meant, and
   * a stale percentage is what the Gantt bar's fill and any future rollup actually read.
   *
   * Both directions, but only where the answer is unambiguous:
   *  - Complete   -> 100%
   *  - Not Started -> 0%
   *  - In Progress, if it was at one of the extremes -> 50%, since neither extreme is "in progress".
   *    Left alone at any other value, because a real 35% shouldn't be overwritten.
   * Blocked never touches the percentage: a task can be blocked at any point in its life.
   */
  const handleStatusChange = (next) => {
    setStatus(next);
    const pct = Number(percentComplete);
    if (next === 'Complete') setPercentComplete('100');
    else if (next === 'Not Started') setPercentComplete('0');
    else if (next === 'In Progress' && (pct === 0 || pct === 100)) setPercentComplete('50');
  };

  /** The mirror: typing 100 or 0 sets the status that matches, so the two can't disagree. */
  const handlePercentChange = (value) => {
    setPercentComplete(value);
    const pct = Number(value);
    if (!statuses.length) return;
    if (pct === 100 && statuses.includes('Complete')) setStatus('Complete');
    else if (pct === 0 && status === 'Complete' && statuses.includes('Not Started')) {
      setStatus('Not Started');
    } else if (pct > 0 && pct < 100 && status !== 'Blocked' && statuses.includes('In Progress')) {
      setStatus('In Progress');
    }
  };

  return (
    <div className="card" style={{ marginTop: 20, borderLeft: '4px solid var(--primary-dark)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="card-title" style={{ marginBottom: 4 }}>{task.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
            {phase ? phase.title : 'Unassigned'}
          </div>
        </div>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
        <Field label="Start">
          <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="End">
          <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="% complete" width={140}>
          <input
            type="number"
            min="0"
            max="100"
            className="form-input"
            value={percentComplete}
            onChange={(e) => handlePercentChange(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select className="form-input" value={status} onChange={(e) => handleStatusChange(e.target.value)}>
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Phase" width={230}>
          <select className="form-input" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            <option value="">— unassigned —</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </Field>
        <Field label="Workstream" width={230}>
          <select
            className="form-input"
            value={workstreamId}
            onChange={(e) => setWorkstreamId(e.target.value)}
          >
            <option value="">— none —</option>
            {workstreams.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </Field>
      </div>

      {(incoming.length > 0 || outgoing.length > 0) && (
        <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-mid)' }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
            }}
          >
            Dependencies
          </div>
          {incoming.map((d) => (
            <DependencyRow
              key={d.id}
              text={`after ${titleOf(d.predecessorId)}`}
              dep={d}
              onDelete={onDeleteDependency}
            />
          ))}
          {outgoing.map((d) => (
            <DependencyRow
              key={d.id}
              text={`before ${titleOf(d.successorId)}`}
              dep={d}
              onDelete={onDeleteDependency}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={() =>
            onSave({
              // Dates are only sent when set: PATCH is partial, and sending an empty string would
              // fail @IsISO8601 rather than clearing the field.
              ...(startDate ? { startDate } : {}),
              ...(endDate ? { endDate } : {}),
              ...(phaseId ? { phaseId } : {}),
              ...(workstreamId ? { workstreamId } : {}),
              percentComplete: Number(percentComplete) || 0,
              status,
            })
          }
        >
          Save changes
        </button>
        {!confirmingDelete ? (
          <button className="btn btn-outline" onClick={() => setConfirmingDelete(true)}>
            Delete task
          </button>
        ) : (
          <>
            <button
              className="btn"
              style={{ background: 'var(--red)', color: 'var(--white)' }}
              onClick={onDelete}
            >
              Confirm delete
            </button>
            <button className="btn btn-outline" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DependencyRow({ text, dep, onDelete }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
      <span>
        ↳ {text}{' '}
        <span style={{ color: 'var(--text-light)' }}>
          ({dep.type}
          {dep.lagDays ? `, ${dep.lagDays}d lag` : ''})
        </span>
      </span>
      <button
        className="btn btn-outline"
        style={{ fontSize: 10, padding: '2px 8px' }}
        onClick={() => onDelete(dep.id)}
      >
        Unlink
      </button>
    </div>
  );
}
